// Chongqing, "Fog and neon over Hongyadong". An autumn night in Fog City, seen from the riverside promenade at
// Jiangbeizui on the north bank of the Jialing River, directly opposite Hongyadong and just west of the Qiansimen
// Bridge's north end (about 29.5695 N, 106.5718 E), looking south-east across the water. Bearings from there (OSM):
// Hongyadong 135-156 degrees, 600 m off, face on, its 210 m front running along the bank; the bridge's single pylon
// about 110 degrees, 320 m off, in the river; Raffles City about 118 degrees, 1.4 km off at Chaotianmen, the tip where
// the Jialing meets the Yangtze; Nan'an and the Nanshan hills beyond the tip. So, left to right: the Nanshan hills and
// the lights of Nan'an across the Yangtze, the Qiansimen Bridge coming in over our heads from the left, its white
// shuttle pylon in the river and its fan of stays, the deck landing on the cliff top at Hongyadong's east end (the road
// on top, Line 6's trains inside the truss below it), Raffles City's towers and the long Crystal on four of them seen
// through the stays, then Hongyadong: eleven storeys of stilt houses stacked up the cliff from the riverside road to the
// cliff top, flared eaves lit gold, red lanterns, pavilion roofs on top, mirrored in the river; the towers of the Yuzhong
// peninsula stacked up the hill behind it with LED facades, a hotpot sign on one of them; the riverside towers going on
// upstream to the right. Fog lies in the tops of the towers and drifts on the river; a drizzle comes and goes.
// The foreground is our promenade: the railing, steps down to the water, two yellow banyans with hanging roots, lamps,
// and the people who come out at night: a woman photographing Hongyadong, a couple at the railing, an angler with a
// lit float on the steps, aunties square-dancing to a little speaker, a chuan chuan cart and its customers.
// Not seen from here: the monorail through the flats at Liziba (far upstream), the Yangtze cable car (the far side of the
// peninsula), the Jiefangbei monument (hidden among the towers), the Grand Theatre (behind us, on our own bank).
// render(t, { crowd }) thins the people on the promenade; crowd 0 draws nobody at all (see CROWD).
//
// The loop's timeline (seconds):
//   all loop  cars and taxis on the bridge's road deck and the riverside road, LED facades cycling slowly on the towers,
//             windows switching on and off, fog drifting at three heights, the drizzle coming and going, the angler's lit
//             float bobbing, the aunties dancing an eight-count with fans, the stall's pot steaming, the photographer
//             raising her phone every 20 s, the couple leaning together every 40 s, the customers eating skewers
//   4-22, 44-62, 84-102, 124-142, 164-182, 204-222   a Line 6 train crosses inside the bridge's lower deck
//   18-104    the big two-rivers cruise ship, lit gold and white, sails down the Jialing towards Chaotianmen (horn at
//             20); the photographer turns to film it (40-60), its wake rocks the angler's float (62-74)
//   60-84     a little river boat runs down close to the far bank (and back up at 200-224)
//   40-52     a scooter rides by (and 166-178 back)
//   84-94     the angler's float dips; he strikes and lifts a small fish
//   96-178    a fog bank rolls in from upstream: the mist thickens first (96-110), the bank swallows the tower tops from
//             right to left (110-150) and frees them again (150-178); the LED facades glow through it
//   124-196   a smaller blue-lit cruise ship comes up the river, left to right (horn at 126)
//   184-230   the riverside towers' LED show: the facades dim (184-188), a slow colour wave sweeps across them, bands
//             rise, the Crystal joins in, then they go back to their own colours (226-230); the aunties stop to watch
//   24-42, 60-78, 104-122, 140-158, 176-194, 200-218   someone crosses the promenade: a man with a bag, two women
//             shopping, two students with bubble tea (in turn)
import { W, H, LOOP, TAU, frac, clamp, lerp, smooth, wave, step, env, cycle, scroll, hash, rng, bayer, hex,
  makeCanvas, makeHalo, drawHalo, streak, makePitch, gradeAd, textWidth, FONT3 } from '../engine/core.js';
import { weatherPlans, drawWeather } from '../engine/weather.js';

let ctx;                                            // the scene's 480x270 context, set by create()
const halo = (hl, x, y, a = 1) => drawHalo(ctx, hl, x, y, a);

/* ================= Layout ================= */
const HZ = 154;                                     // the far shore's waterline at Nan'an, across the Yangtze (the horizon)
const WL = 160;                                     // the Yuzhong bank's waterline under Hongyadong
const ROAD = 153;                                   // the riverside road's deck along the Yuzhong bank: Hongyadong stands on it
const RAIL_T = 190, GND = 203;                      // our railing's top rail; the promenade starts at its foot
const EDGE = 206;                                   // feet of the people standing at the railing
const LANE_A = 230, LANE_B = 252;                   // feet rows of the dancers' plaza and of the walkers' lane
// Hongyadong: eleven storeys of 4 px from the riverside road to the cliff top (79 m), 122 px along the bank (210 m)
const HY = { x0: 230, x1: 352, base: ROAD, fl: 4, floors: 11, top: 104 };
const CLIFF = ROAD - HY.fl * HY.floors;             // the cliff top, Cangbai Road: row 109
// the Qiansimen Bridge's shuttle pylon: its foot in the river, the deck through its widest point, the needle's tip
const PY = { x: 112, base: 170, deck: 124, top: 47 };
const DECK_END = 228;                               // where the deck lands on the cliff top beside Hongyadong
const deckTop = x => Math.round(x <= PY.x ? lerp(128, PY.deck, x / PY.x) : lerp(PY.deck, CLIFF + 1, (x - PY.x) / (DECK_END - PY.x)));
const deckTh = x => Math.max(3, Math.round(lerp(10, 3, clamp(x / DECK_END, 0, 1))));
const PIER = { x: 205 };                            // the girder span's pier near the far bank
const RF = { x0: 126, x1: 204 };                    // Raffles City at Chaotianmen
const BB = { x: 298, y: 72, w: 52, h: 20 };         // the hotpot sign on a tower behind Hongyadong (the city's billboard)
const BBT = { x0: 291, x1: 357, top: 34 };          // that tower
const ACCENT = '#00fff0';                           // the bridge's LED cyan

/* ================= Timeline ================= */
const TRAINS = [[4, 22, -1], [44, 62, 1], [84, 102, -1], [124, 142, 1], [164, 182, -1], [204, 222, 1]];   // t0, t1, dir
const CRUISE = { t0: 18, t1: 104, horn: 20 };       // the big two-rivers cruise ship, right to left
const CRUISE2 = { t0: 124, t1: 196, horn: 126 };    // a smaller blue-lit one, left to right
const FERRY = [{ t0: 60, t1: 84, dir: -1 }, { t0: 200, t1: 224, dir: 1 }];   // a little river boat close to the far bank
const FOG = { t0: 96, in: 110, full: 150, t1: 178 };   // the fog bank: mist thickens, swallows the tops, frees them
const SHOW = { t0: 184, t1: 230 };                  // the riverside towers' LED show
const CATCH = [84, 94];                             // the angler lands a fish
const SCOOT = [{ t0: 40, t1: 52, dir: 1 }, { t0: 166, t1: 178, dir: -1 }];
const WALKS = [{ t0: 24, t1: 42, dir: 1, who: 1 }, { t0: 60, t1: 78, dir: -1, who: 0 }, { t0: 104, t1: 122, dir: 1, who: 2 }, { t0: 140, t1: 158, dir: 1, who: 1 },
  { t0: 176, t1: 194, dir: -1, who: 0 }, { t0: 200, t1: 218, dir: -1, who: 2 }];
const FILM = [40, 60];                              // the photographer turns to film the cruise ship
// The people the scene shows by default: about half. render(t, { crowd }) thins them by rank; crowd 0 is the visitors'
// "None": nobody at all, and what is theirs goes with them (the rod and its float, the speaker, the stall's customers'
// bowls). The cart stays, unattended; the boats keep their skippers out of sight.
const CROWD = 0.5;
const nobody = crowd => crowd === 0;
const RANK = { photo: 0, vendor: 0, angler: 0.1, couple: 0.2, scoot: 0.3, eaters: 0.36, dance: 0.44, walk0: 0.55, kid: 0.62,
  tripod: 0.7, walk1: 0.78, phone: 0.84, walk2: 0.9, leaner: 0.95 };

/* ================= Pixel helpers ================= */
const RGB = {};
const C = s => RGB[s] || (RGB[s] = hex(s));
const toHex = (r, g, b) => '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
const MIXED = {};
function mixHex(a, b, t) {
  const k = a + b + t;
  if (MIXED[k]) return MIXED[k];
  const A = C(a), B = C(b);
  return (MIXED[k] = toHex(...A.map((v, i) => Math.round(lerp(v, B[i], clamp(t, 0, 1))))));
}
/** ImageData-backed painter for prerendering */
function pix(w, h) {
  const [c, x] = makeCanvas(w, h), img = x.createImageData(w, h), d = img.data;
  return {
    c, x, w, h, d,
    set(i, j, col, a = 255) {
      i = Math.round(i); j = Math.round(j);
      if (i < 0 || j < 0 || i >= w || j >= h) return;
      const o = (j * w + i) * 4, v = typeof col === 'string' ? C(col) : col;
      d[o] = v[0]; d[o + 1] = v[1]; d[o + 2] = v[2]; d[o + 3] = a;
    },
    get(i, j) { const o = (j * w + i) * 4; return [d[o], d[o + 1], d[o + 2], d[o + 3]]; },
    alpha(i, j) { return (i < 0 || j < 0 || i >= w || j >= h) ? 0 : d[(j * w + i) * 4 + 3]; },
    fill(i, j, ww, hh, col) { for (let y = j; y < j + hh; y++) for (let k = i; k < i + ww; k++) this.set(k, y, col); },
    line(x0, y0, x1, y1, col) {
      const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0), 1);
      for (let k = 0; k <= n; k++) this.set(lerp(x0, x1, k / n), lerp(y0, y1, k / n), col);
    },
    done() { x.putImageData(img, 0, 0); return c; },
  };
}
/** ordered-dithered pick from a colour ramp, v in 0..1 (soft < 1 narrows the dithered band) */
function pick(ramp, v, i, j, soft = 1) {
  const f = clamp(v, 0, 1) * (ramp.length - 1);
  let k = Math.floor(f);
  if (f - k > 0.5 + (bayer(i, j) - 0.5) * soft) k++;
  return ramp[Math.min(k, ramp.length - 1)];
}
/** smooth value noise in 0..1 (build time only); optional period px in x for tileable layers */
function vnoise(x, y, s, px = 0) {
  const xi = Math.floor(x), yi = Math.floor(y), fx = x - xi, fy = y - yi;
  const X0 = px ? ((xi % px) + px) % px : xi, X1 = px ? (((xi + 1) % px) + px) % px : xi + 1;
  const a = hash(X0, yi, s), b = hash(X1, yi, s), c = hash(X0, yi + 1, s), d = hash(X1, yi + 1, s);
  const u = fx * fx * (3 - 2 * fx), v = fy * fy * (3 - 2 * fy);
  return lerp(lerp(a, b, u), lerp(c, d, u), v);
}
/** sprite from rows of characters and a colour key ('.' or unknown = transparent) */
function sprite(rows, key, flip = false) {
  const h = rows.length, w = Math.max(...rows.map(r => r.length)), p = pix(w, h);
  rows.forEach((row, y) => { for (let i = 0; i < row.length; i++) { const col = key[row[i]]; if (col) p.set(flip ? w - 1 - i : i, y, col); } });
  return p.done();
}
/** crop a painter to its opaque bounding box (build time) */
function trim(p) {
  let x0 = p.w, y0 = p.h, x1 = -1, y1 = -1;
  for (let y = 0; y < p.h; y++) for (let i = 0; i < p.w; i++) if (p.d[(y * p.w + i) * 4 + 3]) {
    if (i < x0) x0 = i; if (i > x1) x1 = i; if (y < y0) y0 = y; if (y > y1) y1 = y;
  }
  const full = p.done();
  if (x1 < 0) return { c: makeCanvas(1, 1)[0], x: 0, y: 0 };
  const [c, x] = makeCanvas(x1 - x0 + 1, y1 - y0 + 1);
  x.drawImage(full, -x0, -y0);
  return { c, x: x0, y: y0 };
}
const flipC = src => { const [c, x] = makeCanvas(src.width, src.height); x.translate(src.width, 0); x.scale(-1, 1); x.drawImage(src, 0, 0); return c; };
const blit = (o, a = 1) => { ctx.globalAlpha = a; ctx.drawImage(o.c, o.x, o.y); ctx.globalAlpha = 1; };
const dot = (x, y, col) => { ctx.fillStyle = col; ctx.fillRect(Math.round(x), Math.round(y), 1, 1); };
const R = (x, y, w, h, c) => { ctx.fillStyle = c; ctx.fillRect(x, y, w, h); };
/** piecewise-linear script: keys [[t, x], ...] sorted by t; the position at t and whether it moves */
function along(keys, t) {
  if (t <= keys[0][0]) return { x: keys[0][1], moving: false };
  for (let i = 1; i < keys.length; i++) if (t < keys[i][0]) {
    const a = keys[i - 1], b = keys[i];
    return { x: lerp(a[1], b[1], (t - a[0]) / (b[0] - a[0])), moving: a[1] !== b[1] };
  }
  return { x: keys[keys.length - 1][1], moving: false };
}
const inWin = (t, a, b) => (t >= a && t < b ? (t - a) / (b - a) : -1);

/* ================= The sky: a low fog sky, lit mauve from below by the city ================= */
const SKY = ['#07081a', '#0a0b1f', '#0e0e25', '#131229', '#18152f', '#1f1935', '#271e3b', '#2f2440', '#392a45', '#433049',
  '#4f374d', '#5b3f51', '#674755'];
function renderSky() {
  const p = pix(W, WL + 4);
  for (let y = 0; y < WL + 4; y++) for (let x = 0; x < W; x++) {
    let v = Math.pow(y / HZ, 1.25) * 0.66;
    v += 0.26 * Math.exp(-(((x - 330) / 170) ** 2)) * smooth(30, 150, y);                // the peninsula's light in the fog
    v += 0.1 * Math.exp(-(((x - 60) / 90) ** 2)) * smooth(90, 150, y);                    // Nan'an's, across the Yangtze
    v += (vnoise(x / 60, y / 9, 3) - 0.5) * 0.06;
    p.set(x, y, pick(SKY, v, x, y));
  }
  return p.done();
}
/* fog in layers, tileable (period W): soft banks of mist, cool on top and mauve where the city lights them from below */
function makeFog(def) {
  const { h, seed } = def, p = pix(W, h), cells = def.cell, per = W / cells;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const n = 0.62 * vnoise(x / cells, y / def.cy, seed, per) + 0.38 * vnoise(x / (cells / 2), y / (def.cy / 2), seed + 1, per * 2);
    const prof = Math.sin(Math.PI * y / h) ** 0.8;
    const v = (n - def.thr) * def.gain * prof;
    const q = Math.floor(clamp(v, 0, 1) * 5 + bayer(x, y)) / 5;
    if (q <= 0) continue;
    p.set(x, y, y < h * 0.45 ? def.top : y < h * 0.75 ? def.mid : def.bot, Math.round(q * def.a * 255));
  }
  return { c: p.done(), y: def.y, k: def.k, h };
}
const FOG_DEFS = [
  { seed: 11, y: -6, h: 74, cell: 48, cy: 14, thr: 0.3, gain: 1.8, a: 0.72, k: 1, top: '#3e3e5c', mid: '#504a68', bot: '#62546e' },   // in the tower tops
  { seed: 23, y: 40, h: 66, cell: 40, cy: 10, thr: 0.4, gain: 1.6, a: 0.46, k: 2, top: '#48445e', mid: '#5a4e6c', bot: '#6e5a72' },   // mid-height haze
  { seed: 37, y: 132, h: 34, cell: 30, cy: 6, thr: 0.4, gain: 1.8, a: 0.4, k: -3, top: '#4a4662', mid: '#5e5470', bot: '#6e6078' },   // mist on the river
];
/* low cloud over the city, tileable (period W): puffy banks, dark on top, lit mauve from below by the city's lights */
function makeCloudLayer(def) {
  const r = rng(def.seed), h = def.h, puffs = [];
  for (let b = 0; b < def.banks; b++) {
    const bx = (b + r() * 0.8) * W / def.banks, bw = 50 + r() * 90, base = h * (0.45 + r() * 0.35);
    const np = 6 + Math.floor(r() * 6);
    for (let i = 0; i < np; i++) {
      const rx = 10 + r() * 22, ry = 3.5 + r() * 6;
      puffs.push({ x: bx + (r() - 0.5) * bw, y: base - r() * ry * 1.2, rx, ry, base: base + ry * 0.3 });
    }
  }
  const D = new Float32Array(W * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    let best = -9;
    for (const q of puffs) {
      let dx = x - q.x; dx -= Math.round(dx / W) * W;
      const dy = y - q.y;
      let v = 1 - (dx * dx) / (q.rx * q.rx) - (dy * dy) / (q.ry * q.ry);
      if (y > q.base) v -= (y - q.base) * 0.35;
      if (v > best) best = v;
    }
    best += 0.15 * Math.sin(TAU * 11 * x / W + y * 0.41 + def.seed) * Math.sin(TAU * 5 * x / W - y * 0.23);
    D[y * W + x] = best;
  }
  const get = (x, y) => (y < 0 || y >= h) ? -9 : D[y * W + ((x % W) + W) % W];
  const p = pix(W, h), P = def.pal;
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++) {
    const th = (bayer(x, y) - 0.5) * 0.3, v = D[y * W + x];
    if (v + th <= 0.03) continue;
    let col;
    if (get(x, y - 1) + th <= 0.03 || get(x, y - 2) <= 0) col = P.top;
    else if (get(x, y + 1) + th <= 0.03) col = P.bot;
    else if (get(x, y + 3) <= 0.05 + bayer(x, y) * 0.2) col = P.bot2;
    else if (v > 0.55 + th) col = P.core;
    else col = P.body;
    p.set(x, y, col, def.a);
  }
  return { c: p.done(), y: def.y, k: def.k };
}
const CLOUD_DEFS = [
  { seed: 13, y: -10, h: 56, banks: 7, k: 1, a: 255, pal: { top: '#2c2a44', body: '#201e36', core: '#1c1a30', bot: '#3e3450', bot2: '#302a44' } },
  { seed: 31, y: 18, h: 60, banks: 6, k: 2, a: 240, pal: { top: '#38344e', body: '#2a2640', core: '#242038', bot: '#5a4458', bot2: '#463a50' } },
];
/** the fog bank that rolls in: 720 px wide, a soft leading edge on the left, thick in the tower tops */
function makeFogBank() {
  const w = 720, h = 96, p = pix(w, h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const n = 0.6 * vnoise(x / 36, y / 11, 51) + 0.4 * vnoise(x / 14, y / 5, 52);
    const edge = smooth(0, 190, x) * (1 - smooth(w - 150, w, x)), prof = 1 - smooth(50, 94, y + (n - 0.5) * 30);
    const v = (0.8 + (n - 0.5) * 0.8) * edge * prof * 1.3;
    const q = Math.floor(clamp(v, 0, 1) * 6 + bayer(x, y)) / 6;
    if (q <= 0) continue;
    p.set(x, y, y < 30 ? '#4a4864' : y < 62 ? '#585270' : '#665a74', Math.round(q * 0.94 * 255));
  }
  return p.done();
}

/* ================= Windows that switch on and off ================= */
const WINS = [];
const LIT = [['#ffe2a8', '#f2c27a'], ['#fff2d6', '#e8d6b0'], ['#dfe8ff', '#b4c4e8'], ['#ffd08a', '#e8a050'], ['#c8f0ff', '#90c8e8']];
function addWin(r, x, y, w, h, opt = {}) {
  const v = r(), on = opt.pOn ?? 0.4, mode = opt.mode || (v < on ? 'on' : v < on + 0.3 ? 'tog' : 'off'), lp = opt.lp || LIT[Math.floor(r() * r() * 5)];
  WINS.push({ x, y, w, h, mode, k: 2 + Math.floor(r() * 4), ph: r(), duty: 0.35 + r() * 0.4, c1: lp[0], c2: lp[1] });
}

/* ================= Across the Yangtze: the Nanshan hills and Nan'an's riverfront ================= */
const NAN_TOWERS = [[8, 9, 104], [19, 7, 116], [28, 11, 98], [41, 8, 110], [52, 10, 92], [64, 7, 118], [74, 9, 106], [86, 8, 121]];   // x, w, top
const ridgeY = x => Math.round(124 + 6 * vnoise(x / 26, 1, 61) + 4 * vnoise(x / 9, 2, 62) + 10 * smooth(60, 140, x));
function paintNanan(p, r) {
  for (let x = 0; x < 150; x++) {                                                        // the hills, a TV mast on the ridge
    const top = ridgeY(x);
    for (let y = top; y < HZ; y++) {
      const v = 0.2 + 0.25 * smooth(top, HZ, y) + (vnoise(x / 4, y / 3, 63) - 0.5) * 0.12;
      p.set(x, y, y === top ? '#2a2a46' : pick(['#0c0d1e', '#111226', '#17182e', '#1e1e36'], v, x, y));
      if (y > top + 3 && hash(x, y, 64) < 0.012 + 0.04 * smooth(top, HZ, y)) p.set(x, y, hash(x, y, 65) < 0.6 ? '#e8b878' : '#c8d0e8');
    }
  }
  for (let y = ridgeY(36) - 9; y < ridgeY(36); y++) p.set(36, y, '#2a2a40');
  for (const [x0, w, top] of NAN_TOWERS) {                                               // Nan'an's riverfront towers
    for (let y = top; y < HZ - 1; y++) for (let x = x0; x < x0 + w; x++) {
      const edge = x === x0 ? 0.08 : x === x0 + w - 1 ? -0.06 : 0;
      p.set(x, y, pick(['#10132a', '#161a34', '#1e223e', '#262a48'], 0.3 + edge + 0.25 * smooth(top + 20, HZ, y), x, y));
    }
    for (let x = x0; x < x0 + w; x++) p.set(x, top, '#3a3e5c');
    for (let wy = top + 3; wy < HZ - 3; wy += 3) for (let wx = x0 + 1; wx < x0 + w - 1; wx += 2) {
      const v = r();
      if (v < 0.24) p.set(wx, wy, v < 0.12 ? '#c8a878' : '#9aa6c8');
      else if (v < 0.3) addWin(r, wx, wy, 1, 1, { mode: 'tog' });
    }
  }
  for (let x = 0; x < 150; x++) { p.set(x, HZ - 1, x % 5 ? '#2a2c44' : '#e8c890'); }    // the far quay's lamps
}
const NAN_LED = [[28, 11, 98], [52, 10, 92]];       // two of Nan'an's towers run LED facades too

/* ================= The Yuzhong peninsula's towers, stacked up the hill behind Hongyadong ================= */
// Back row on the ridge (bases hidden by the buildings on the cliff top), then the riverside towers upstream on the
// right, whose feet reach the riverside road. Every tower: dark glass, a lit edge, a window grid; some run LED facades
// (LED: per frame), some have a lit crown, all of the tall ones a red aviation lamp.
const TOWERS = [];
const LEDS = [];                                    // LED facades: { x, y, w, h, mode, hue, ph, id, show }
const AIR = [];                                     // aviation lamps
function buildTowers(r) {
  TOWERS.length = LEDS.length = AIR.length = 0;
  let x = 198, i = 0;
  while (x < W + 4) {                                                                    // the ridge row
    const w = 11 + Math.floor(r() * 10), mid = x + w / 2;
    let top = 26 + 46 * (1 - Math.exp(-(((mid - 345) / 105) ** 2))) + r() * 16;
    if (mid < 232) top = 58 + r() * 14;
    if (mid > 440) top = 44 + r() * 18;
    if (mid > BBT.x0 - 4 && mid < BBT.x1 + 4) { x = BBT.x1 + 1; continue; }              // the sign's tower is drawn on its own
    TOWERS.push({ x0: x, x1: x + w - 1, top: Math.round(top), base: 122, style: ['flat', 'step', 'spire', 'slant', 'crown'][Math.floor(r() * 5)], id: i++, tone: r() });
    x += w + (r() < 0.4 ? 1 + Math.floor(r() * 3) : -Math.floor(r() * 3));
  }
  TOWERS.push({ x0: BBT.x0, x1: BBT.x1, top: BBT.top, base: 122, style: 'step', id: i++, tone: 0.45, sign: true });
  for (const [x0, w, top] of [[362, 16, 58], [381, 12, 74], [396, 18, 50], [418, 13, 82], [434, 17, 62], [455, 14, 88], [468, 16, 70]])
    TOWERS.push({ x0, x1: x0 + w - 1, top, base: ROAD - 1, style: ['slant', 'flat', 'crown', 'step', 'spire', 'flat', 'crown'][TOWERS.length % 7], id: i++, tone: r(), front: true });
}
const TOWER_PAL = [['#0b0e22', '#10142a', '#161b34', '#1d233e', '#262c4a'], ['#0d0d20', '#131328', '#1a1a32', '#22223e', '#2c2a4a']];
function paintTower(p, r, T) {
  const pal = TOWER_PAL[T.tone < 0.5 ? 0 : 1], w = T.x1 - T.x0 + 1;
  const topAt = x => {                                                                   // the roofline of each style
    const u = (x - T.x0) / Math.max(1, w - 1);
    if (T.style === 'slant') return T.top + Math.round(u * 7);
    if (T.style === 'step') return T.top + (u < 0.2 || u > 0.8 ? 6 : 0);
    if (T.style === 'spire') return T.top + Math.round(Math.abs(u - 0.5) * 8);
    return T.top;
  };
  for (let x = T.x0; x <= T.x1; x++) {
    const t0 = topAt(x);
    for (let y = t0; y < T.base; y++) {
      let v = 0.25 + 0.3 * smooth(T.top + 10, T.base + 30, y) + (T.tone - 0.5) * 0.18;
      if (x === T.x0) v += 0.22; else if (x === T.x1) v -= 0.12;
      p.set(x, y, y === t0 ? '#4a5272' : pick(pal, v, x, y, 0.6));
    }
  }
  if (T.style === 'spire') { const cx = Math.round((T.x0 + T.x1) / 2); for (let y = T.top - 9; y < T.top; y++) p.set(cx, y, '#3a4060'); AIR.push([cx, T.top - 10, T.id]); }
  else if (T.top < 70) AIR.push([T.x0 + 2 + (T.id % 3), topAt(T.x0 + 2) - 1, T.id]);
  if (T.style === 'flat' && w > 12) for (let y = T.top - 3; y < T.top; y++) for (let x = T.x0 + 3; x < T.x0 + 8; x++) p.set(x, y, '#20243a');
  // the window grid: floors every 2-3 rows, some floors dark, the lit ones warm, white or cool
  const pitch = 2 + (T.id % 2), led = !T.sign && (T.id % 3 === 1 || (T.front && T.id % 2 === 0));
  for (let y = topAt(T.x0) + 3; y < T.base - 1; y += pitch) {
    if (r() < 0.12) continue;
    for (let x = T.x0 + 1; x < T.x1; x += 2) {
      const v = r();
      if (v < 0.2) p.set(x, y, v < 0.08 ? '#d8b880' : v < 0.15 ? '#a8b4d0' : '#6a7aa0');
      else if (v < 0.235 && y > 40) addWin(r, x, y, 1, 1, { mode: 'tog', lp: LIT[Math.floor(r() * 5)] });
    }
  }
  if (T.style === 'crown') for (let x = T.x0; x <= T.x1; x++) { p.set(x, T.top + 1, '#8a9ad0'); p.set(x, T.top + 2, '#3a4468'); }
  if (led) {                                                                             // an LED facade: a band or the whole face
    const full = T.id % 2 === 0, y0 = topAt(T.x0) + 4, y1 = full ? T.base - 4 : Math.min(T.base - 4, y0 + 18 + (T.id % 4) * 6);
    LEDS.push({ x: T.x0 + 1, y: y0, w: w - 2, h: y1 - y0, mode: T.id % 3, hue: T.id * 0.37 % 1, ph: hash(T.id, 3, 71), id: T.id, front: !!T.front });
  }
}

/* ================= Raffles City at Chaotianmen: six of its towers and the Crystal laid across four of them ================= */
// From here the two 350 m towers stand in front (north, on the Jialing side), the Crystal on its four 250 m towers behind
// them, and the two towers at the tip on the left. Drawn at 1.4 km: the Crystal 300 m long at about 250 m up.
const RAFF = [
  { x0: 128, x1: 134, top: 92, sail: -1 }, { x0: 136, x1: 142, top: 90, sail: -1 },     // the tip's two towers
  { x0: 148, x1: 153, top: 86 }, { x0: 160, x1: 165, top: 86 }, { x0: 178, x1: 183, top: 86 }, { x0: 192, x1: 197, top: 86 },   // under the Crystal
];
const RAFF_TALL = [{ x0: 154, x1: 162, top: 68, sail: 1 }, { x0: 168, x1: 176, top: 66, sail: 1 }];
const CRYSTAL = { x0: 146, x1: 199, y0: 80, y1: 86 };
function paintRaffles(p) {
  const tower = (T, near) => {
    const w = T.x1 - T.x0 + 1;
    for (let y = T.top; y < 152; y++) for (let x = T.x0; x <= T.x1; x++) {
      const u = (x - T.x0) / (w - 1), k = y - T.top;
      if (T.sail && k < 7 && (T.sail > 0 ? u > 0.3 + k * 0.12 : u < 0.7 - k * 0.12)) continue;   // the sail-like curve at the top
      let v = 0.3 + 0.25 * smooth(T.top, 150, y) + (u < 0.3 ? 0.18 : u > 0.75 ? -0.1 : 0) + (near ? 0.08 : 0);
      let col = pick(['#0e1428', '#141c34', '#1c2540', '#26304e', '#323c5c'], v, x, y, 0.6);
      if ((x - T.x0) % 3 === 1 && y > T.top + 2) col = mixHex(col, '#9aaed0', 0.18);    // its vertical fins catch the light
      p.set(x, y, col);
    }
    for (let x = T.x0; x <= T.x1; x++) { const u = (x - T.x0) / (w - 1); for (let k = 0; k < 8; k++) if (!(T.sail && k < 7 && (T.sail > 0 ? u > 0.3 + k * 0.12 : u < 0.7 - k * 0.12))) { p.set(x, T.top + k, '#c8d4ec'); break; } }
  };
  for (const T of RAFF) tower(T, false);
  for (let y = CRYSTAL.y0; y <= CRYSTAL.y1; y++) {                                       // the Crystal: a glass tube, lit from inside
    const inset = y === CRYSTAL.y0 || y === CRYSTAL.y1 ? 2 : y === CRYSTAL.y0 + 1 ? 1 : 0;
    for (let x = CRYSTAL.x0 + inset; x <= CRYSTAL.x1 - inset; x++) {
      const col = y === CRYSTAL.y0 ? '#9ab4d4' : y === CRYSTAL.y1 ? '#3a4a6a' : (x % 4 === 0 ? '#5a7090' : pick(['#4a6488', '#6a88aa', '#8aa8c8'], 0.3 + 0.4 * ((y - CRYSTAL.y0) / 6), x, y));
      p.set(x, y, col);
    }
  }
  for (const T of RAFF_TALL) tower(T, true);
  for (const T of RAFF_TALL) AIR.push([Math.round((T.x0 + T.x1) / 2) - 1, T.top - 1, 90 + T.x0]);
}

/* ================= The cliff top and the old town between the towers and the river ================= */
// Mid-rise blocks along Cangbai Road on the cliff top, showing over Hongyadong's roofs; the buildings stepping down to
// the riverside road towards Chaotianmen on the left; the cliff itself where Hongyadong doesn't cover it.
function paintRidge(p, r) {
  let x = 196;
  while (x < 362) {
    const w = 8 + Math.floor(r() * 12), top = 92 + Math.floor(r() * 18), col = ['#1c1a2e', '#221e32', '#1a1e30', '#262236'][Math.floor(r() * 4)];
    for (let y = top; y < 130; y++) for (let i = x; i < Math.min(W, x + w); i++)
      p.set(i, y, y === top ? '#3c3a54' : i === x ? mixHex(col, '#5a5470', 0.3) : pick([mixHex(col, '#05060f', 0.4), col, mixHex(col, '#ff9a5a', 0.06)], 0.35 + 0.4 * smooth(top, 130, y), i, y));
    for (let wy = top + 3; wy < 128; wy += 3) for (let wx = x + 2; wx < Math.min(W, x + w) - 1; wx += 2) {
      const v = r();
      if (v < 0.26) p.set(wx, wy, v < 0.12 ? '#f0c888' : v < 0.2 ? '#e8e0d0' : '#a8c0e0');
      else if (v < 0.3) addWin(r, wx, wy, 1, 1, { mode: 'tog' });
    }
    if (r() < 0.3) { const sx = x + 1 + Math.floor(r() * (w - 3)), sc = ['#ff5a6a', '#5ad0ff', '#ffb04a'][Math.floor(r() * 3)]; for (let y = top + 3; y < top + 11; y++) p.set(sx, y, mixHex(sc, '#1a1a2a', 0.35)); }   // a vertical shop sign, no words
    x += w;
  }
  // Chaotianmen's side of the bank, under the deck: old blocks and retaining walls stepping down to the riverside road
  x = 118;
  while (x < 228) {
    const w = 6 + Math.floor(r() * 9), top = Math.round(lerp(140, 118, (x - 118) / 110) + r() * 8);
    for (let y = top; y < ROAD; y++) for (let i = x; i < Math.min(228, x + w); i++)
      p.set(i, y, y === top ? '#3a3850' : pick(['#0e0f20', '#14152a', '#1c1c34', '#262438'], 0.25 + 0.45 * smooth(top, ROAD, y) + (i === x ? 0.15 : 0), i, y));
    for (let wy = top + 2; wy < ROAD - 2; wy += 3) for (let wx = x + 1; wx < Math.min(228, x + w) - 1; wx += 2) { const v = r(); if (v < 0.3) p.set(wx, wy, v < 0.16 ? '#e8b878' : '#c8d0e8'); else if (v < 0.34) addWin(r, wx, wy, 1, 1, { mode: 'tog' }); }
    x += w;
  }
  // the cliff where the buildings leave it bare: dark rock, ferns and small trees, lit from the road below
  for (let x = 352; x < 364; x++) for (let y = CLIFF - 6 + Math.round(3 * vnoise(x / 3, 1, 81)); y < ROAD; y++) {
    const n = vnoise(x / 2.5, y / 2.5, 82), green = n > 0.5;
    p.set(x, y, green ? pick(['#080e0c', '#0e1812', '#162418', '#24321e'], 0.15 + 0.35 * smooth(CLIFF, ROAD, y) + (n - 0.5) * 0.6, x, y) : pick(['#120e14', '#1c1418', '#2a1c1c', '#3a2620'], 0.3 + 0.4 * smooth(CLIFF, ROAD, y), x, y));
  }
  for (let x = 222; x < HY.x0; x++) for (let y = CLIFF; y < ROAD; y++) p.set(x, y, pick(['#120e14', '#1c1418', '#2a1c1c', '#3a2620'], 0.25 + 0.5 * smooth(CLIFF, ROAD, y) + (vnoise(x, y / 2, 83) - 0.5) * 0.3, x, y));
}

/* ================= Hongyadong: stilt houses stacked eleven storeys up the cliff, flared eaves lit gold ================= */
// The ratios it is drawn to (79 m tall, 11 storeys, its main front about 210 m along the bank): the storeys fill the cliff
// from the riverside road (row 153) to Cangbai Road on the cliff top (row 109), 4 px each; the front is 122 px long, so
// the storeys' height to the length is 1 : 2.8 and with the roofs on top 1 : 2.3 (real 1 : 2.66 to the cliff top).
// Every storey of 4 rows: its own pent roof of dark tiles, the gold-lit eave under it, then two rows of lit windows
// between dark posts. The houses step up and down the front, some standing two rows higher on stilts, the upper
// storeys set back; each eave flares past its house's end and turns up at the tip; roofs and pavilions on top.
// [x0, x1 relative to HY.x0, storeys, rows up on stilts, roof]
const HY_SECT = [[0, 12, 11, 0, 'hall'], [12, 22, 9, 2, null], [22, 33, 10, 0, 'hall'], [33, 55, 11, 0, 'pav2'], [55, 67, 9, 2, 'hall'],
  [67, 97, 11, 0, 'pav'], [97, 107, 10, 2, null], [107, 122, 8, 0, 'hall']];
const EAVE = ['#8a4a1a', '#c8742a', '#ffb43c', '#ffd264', '#fff0b4'];
const TILE = ['#1a0e10', '#26161a', '#34201e', '#462a22'];
const WINC = ['#ffd27a', '#ffb44a', '#ff9238', '#e8642a', '#b8401e', '#4a1a10'];
const HY_LANTERNS = [];                             // red lanterns under the eaves (they sway a pixel now and then, per frame)
const HY_FLICK = [];                                // windows where someone moves about (per frame)
const flY = f => ROAD - HY.fl * (f + 1);            // the top row of storey f (0 at the road)
const hySect = s => {                               // a house's columns on its top storey, after the set-backs
  const [a, b, n] = s, set = Math.floor(Math.max(0, n - 1 - 4) / 3);
  return [HY.x0 + a + (a > 0 ? set : 0), HY.x0 + b - 1 - (b < 122 ? set : 0)];
};
function paintHongyadong(p, r) {
  const X = HY.x0, top = (s, f) => flY(f) - s[3];
  const coversAt = (x, y) => HY_SECT.some(s => x >= X + s[0] && x < X + s[1] && y >= top(s, s[2] - 1));
  HY_SECT.forEach((s, si) => {
    const [a, b, n, up] = s, hot = 0.55 + 0.45 * Math.exp(-(((X + (a + b) / 2 - 292) / 60) ** 2));
    const shop = [0, 1, 2, 1, 0, 2, 1, 3][si];
    for (let f = 0; f < n; f++) {
      const y0 = top(s, f), set = f > 4 ? Math.floor((f - 4) / 3) : 0;
      const x0 = X + a + (a > 0 ? set : 0), x1 = X + b - 1 - (b < 122 ? set : 0);
      for (let x = x0; x <= x1; x++) {
        const u = x - x0;
        p.set(x, y0, pick(TILE, 0.1 + 0.25 * (u % 2) + 0.15 * hot, x, y0));                       // the pent roof's tiles
        const post = u % 3 === 0 || x === x1, balc = (f + si) % 3 === 1;
        for (let k = 2; k <= 3; k++) {
          let col;
          if (post) col = k === 2 ? '#3a160c' : '#2a0e08';
          else {
            const v = hash(Math.floor(u / 3), f, 91 + shop), w = v < 0.1 ? 5 : v < 0.22 ? 3 : v < 0.6 ? 1 : v < 0.85 ? 2 : 0;
            col = WINC[Math.min(5, w + (k === 3 && w < 3 ? 1 : 0) + (hot < 0.7 && w < 3 ? 1 : 0))];
          }
          if (k === 3 && balc && !post) col = u % 2 ? EAVE[2] : '#3a160c';                    // a balcony's lit balusters
          p.set(x, y0 + k, col);
        }
        if (!post && hash(x, f, 92) < 0.05) HY_FLICK.push({ x, y: y0 + 2, id: HY_FLICK.length });
      }
      // the eave: a gold line one pixel past each end, its tip turned up where nothing stands beside it
      for (let x = x0 - 1; x <= x1 + 1; x++) p.set(x, y0 + 1, pick(EAVE, 0.66 + 0.26 * hot, x, y0 + 1, 0.4));
      if (!coversAt(x0 - 2, y0 + 1)) p.set(x0 - 2, y0, EAVE[4]);
      if (!coversAt(x1 + 2, y0 + 1)) p.set(x1 + 2, y0, EAVE[4]);
      for (let x = x0 + 2; x < x1 - 1; x += 5 + ((x + f) % 4)) if ((f + si) % 2 === 0) HY_LANTERNS.push({ x, y: y0 + 2, id: HY_LANTERNS.length });
    }
    for (let k = 0; k < up; k++) for (let x = X + a; x < X + b; x++) {                       // the stilts under a raised house
      const xs = x - X - a;
      p.set(x, ROAD - 1 - k, xs % 3 === 0 ? '#3a1a10' : xs % 3 === 1 && k === 0 ? '#ff9a3a' : '#1a0a08');
    }
  });
  // the roofs on top: dark tiles, gold eaves and ridges, flared corners; long halls, a two-tier pavilion, a tall one
  const roof = (x0, x1, yb, rows, gold) => {
    for (let j = 0; j < rows; j++) {
      const y = yb - j, inset = j === 0 ? -1 : Math.round(j * 1.5);
      for (let x = x0 + inset; x <= x1 - inset; x++) p.set(x, y, j === 0 ? pick(EAVE, gold ? 0.95 : 0.8, x, y, 0.4) : j === rows - 1 ? EAVE[3] : pick(TILE, 0.4 + 0.4 * ((x + j) % 2), x, y));
    }
    p.set(x0 - 2, yb - 1, EAVE[4]); p.set(x1 + 2, yb - 1, EAVE[4]);                          // the flared corners
    const yr = yb - rows + 1, ir = Math.round((rows - 1) * 1.5);
    p.set(x0 + ir - 1, yr - 1, EAVE[4]); p.set(x1 - ir + 1, yr - 1, EAVE[4]);                // the ridge's upturned ends
  };
  const band = (x0, x1, y) => { for (let x = x0; x <= x1; x++) { p.set(x, y, x % 3 === 0 ? '#3a160c' : WINC[1]); p.set(x, y + 1, x % 3 === 0 ? '#2a0e08' : WINC[2]); } };
  for (const s of HY_SECT) {
    const kind = s[4];
    if (!kind) continue;
    const [x0, x1] = hySect(s), yb = flY(s[2] - 1) - s[3] - 1;
    if (kind === 'hall') roof(x0, x1, yb, 3, false);
    else if (kind === 'pav') { roof(x0, x1, yb, 3, false); band(x0 + 7, x1 - 7, yb - 4); roof(x0 + 7, x1 - 7, yb - 5, 4, true); }
    else {
      roof(x0, x1, yb, 3, false); band(x0 + 5, x1 - 5, yb - 4); roof(x0 + 5, x1 - 5, yb - 5, 3, true);
      const cx = Math.round((x0 + x1) / 2); for (let y = yb - 8; y >= yb - 10; y--) p.set(cx, y, EAVE[4]);
    }
  }
}
/* the lower stilt houses that carry on along the bank beyond Hongyadong's main front, dimmer */
function paintWings(p) {
  const wing = (x0, x1, n) => {
    for (let f = 0; f < n; f++) {
      const y0 = flY(f);
      for (let x = x0; x <= x1; x++) {
        p.set(x, y0, pick(TILE, 0.5, x, y0));
        p.set(x, y0 + 1, pick(EAVE, 0.4, x, y0 + 1, 0.5));
        for (let k = 2; k <= 3; k++) p.set(x, y0 + k, x % 3 === 0 ? '#2a0e08' : WINC[2 + Math.floor(hash(x >> 1, f, 93) * 4)]);
      }
      p.set(x0 - 2, y0, EAVE[2]); p.set(x1 + 2, y0, EAVE[2]);
    }
  };
  wing(353, 366, 3); wing(214, 227, 2);
}
/* the riverside road along the Yuzhong bank: its deck, lamps, the stone embankment down to the water */
function paintRoad(p) {
  for (let x = RF.x0 - 8; x < W; x++) {
    p.set(x, ROAD, '#4a4658'); p.set(x, ROAD + 1, '#242232'); p.set(x, ROAD + 2, '#1a1826');
    p.set(x, ROAD + 3, x % 7 === 3 ? '#ffe2a0' : '#3a3648');                              // the road's lamps on its parapet
    for (let y = ROAD + 4; y < WL; y++) {
      const lit = Math.exp(-((((x % 7) - 3) / 2.2) ** 2)) * (1 - (y - ROAD - 4) / 5);
      p.set(x, y, pick(['#14121e', '#1e1a26', '#2c2430', '#4a3a3a', '#7a5a4a'], 0.2 + 0.5 * lit + ((x >> 2) + y) % 3 * 0.03, x, y));
    }
  }
}

/* ================= The Qiansimen Bridge: one shuttle pylon, one plane of stays, a road on top and Line 6 below ================= */
// The ratios it is drawn to: the deck about 60 m over the river and the pylon's tip 100 m over the deck (TY Lin); the
// pylon 18 m wide at its foot, 35 m where the deck passes through it, 7 m at the top, so a shuttle. Here the deck
// crosses the pylon 46 px over its foot and the tip stands 77 px over the deck (1 : 1.67, as real); 7, 13 and 2 px
// wide. Nine stays a side. The main span runs off to the right to the far bank, the back span towards us on the left;
// the deck is a steel truss 13 m deep, the trains run inside its lower half.
const STAYS_MAIN = [], STAYS_BACK = [];
const pyHW = y => y < PY.deck ? lerp(1, 6.5, Math.pow((y - PY.top) / (PY.deck - PY.top), 0.85)) : lerp(6.5, 3.6, (y - PY.deck) / (PY.base - PY.deck));
function renderBridge(withDeck = true) {
  const p = pix(W, H);
  STAYS_MAIN.length = STAYS_BACK.length = 0;
  // the girder span's pier by the far bank, and the abutment where the deck enters the cliff
  for (let y = deckTop(PIER.x) + deckTh(PIER.x); y <= WL; y++) for (let x = PIER.x - 1; x <= PIER.x + 1; x++) p.set(x, y, x === PIER.x - 1 ? '#5a6078' : x === PIER.x ? '#3a4058' : '#22263a');
  // the pylon: floodlit pale concrete, lit from the left and below, its edge catching the light
  for (let y = PY.top; y <= PY.base; y++) {
    const hw = pyHW(y), x0 = Math.round(PY.x - hw), x1 = Math.round(PY.x + hw);
    for (let x = x0; x <= x1; x++) {
      const u = (x - x0) / Math.max(1, x1 - x0), up = smooth(PY.top, PY.base, y);
      let v = 0.78 - u * 0.62 + 0.12 * up - (y > PY.deck + 8 ? 0.18 : 0);
      if (x === x0) v += 0.12;
      p.set(x, y, pick(['#1a2034', '#3a4460', '#5e6a88', '#8a96b2', '#b4bed4', '#d2dae8'], v, x, y, 0.6));
    }
  }
  for (let y = PY.deck - 18; y < PY.deck; y++) p.set(PY.x, y, '#2a3048');               // the slot where the stays meet
  for (let y = PY.base - 4; y <= PY.base + 1; y++) for (let x = PY.x - 7; x <= PY.x + 7; x++) p.set(x, y, y === PY.base - 4 ? '#6a7088' : pick(['#1a1e2e', '#262a3c', '#363a4e'], 0.6 - (x - PY.x + 7) / 20, x, y));   // its foot in the river
  if (!withDeck) return p.done();
  // the stays: nine a side, fanning from the pylon's upper half to the deck
  for (let k = 0; k < 9; k++) {
    const ay = Math.round(PY.deck - 26 - k * 5.4);
    const xm = Math.round(PY.x + 13 + k * 8.4), xb = Math.round(PY.x - 13 - k * 11);
    STAYS_MAIN.push([PY.x, ay, xm, deckTop(xm)]); STAYS_BACK.push([PY.x, ay, xb, deckTop(xb)]);
  }
  for (const [x0, y0, x1, y1] of [...STAYS_MAIN, ...STAYS_BACK]) {
    const n = Math.max(Math.abs(x1 - x0), Math.abs(y1 - y0));
    for (let s = 1; s < n; s++) {
      const x = Math.round(lerp(x0, x1, s / n)), y = Math.round(lerp(y0, y1, s / n));
      if (Math.abs(x - PY.x) <= pyHW(y)) continue;
      p.set(x, y, '#aebad4', 120);
    }
  }
  // the deck: the road's parapet on top, the truss below it (its diagonals catch the light), amber lamps on the lower chord
  for (let x = 0; x <= DECK_END; x++) {
    const y0 = deckTop(x), th = deckTh(x), panel = Math.max(3, Math.round(lerp(10, 3, x / DECK_END)));
    if (Math.abs(x - PY.x) <= 6 && x !== PY.x - 7) {                                     // through the pylon
      p.set(x, y0 + 1, '#8a96b0');
      for (let k = 2; k < th; k++) p.set(x, y0 + k, '#1a2034');
      continue;
    }
    p.set(x, y0 + 1, '#6a7490');
    for (let k = 2; k < th - 1; k++) {
      const ph = (x % panel) / panel, diag = Math.abs(ph - (k - 2) / Math.max(1, th - 4)) < 0.5 / panel + 0.08 || Math.abs((1 - ph) - (k - 2) / Math.max(1, th - 4)) < 0.5 / panel + 0.08;
      p.set(x, y0 + k, diag ? '#3c4662' : x % panel === 0 ? '#323a54' : '#0c1020');
    }
    p.set(x, y0 + th - 1, x % 5 === 2 && x < DECK_END - 6 ? '#ffb45a' : '#2e3650');
  }
  for (let y = CLIFF - 1; y < CLIFF + 9; y++) for (let x = DECK_END - 5; x <= DECK_END + 1; x++) p.set(x, y, pick(['#1a1c2a', '#2a2c3c', '#3a3c4e'], 0.5 - (y - CLIFF) * 0.04, x, y));
  return p.done();
}
// the cars on the road deck: toward us (headlights) and away (tail lights); [dir, crossings a loop, phase]
const DECK_CARS = [[-1, 15, 0.05], [-1, 12, 0.41], [-1, 14, 0.72], [-1, 10, 0.23], [1, 13, 0.15], [1, 11, 0.55], [1, 16, 0.86], [1, 9, 0.35]];
const ROAD_CARS = [[-1, 9, 0.1], [-1, 7, 0.6], [1, 8, 0.3], [1, 10, 0.8], [-1, 6, 0.45]];   // the riverside road under Hongyadong
const deckX = s => Math.round(DECK_END - 4 - (DECK_END + 14) * Math.pow(s, 1.25));       // s 0 at the far end .. 1 past the left edge
function drawDeckTraffic(t) {
  for (const [d, k, ph] of DECK_CARS) {
    const u = frac(k * t / LOOP + ph), s = d < 0 ? u : 1 - u, x = deckX(s), y = deckTop(x);
    if (x < 0 || x > DECK_END - 3 || Math.abs(x - PY.x) <= 6) continue;
    const big = s > 0.55 ? 1 : 0;
    if (d < 0) { R(x, y, 1 + big, 1, '#fff6dc'); if (big) halo(HL.head, x, y, 0.5); }
    else R(x, y, 1 + big, 1, '#ff4a3a');
  }
}
/** a Line 6 train inside the lower deck: its lit windows through the truss, cars and gaps scaled by the distance */
function drawTrain(t) {
  for (const [t0, t1, dir] of TRAINS) {
    const u = inWin(t, t0, t1);
    if (u < 0) continue;
    const len = 0.42, f = lerp(-len, 1.05, u), sHead = dir < 0 ? f + len : 1 - f, sTail = sHead - len;
    const xa = deckX(clamp(Math.max(sHead, sTail), 0, 1)), xb = deckX(clamp(Math.min(sHead, sTail), 0, 1));
    for (let x = Math.max(0, Math.min(xa, xb)); x <= Math.min(DECK_END - 6, Math.max(xa, xb)); x++) {
      if (Math.abs(x - PY.x) <= 6) continue;
      const y0 = deckTop(x), th = deckTh(x), rows = Math.max(1, th - 5), cars = 6;
      const q = (x - Math.min(xa, xb)) / Math.max(1, Math.abs(xa - xb)), c = frac(q * cars);
      if (c > 0.93 || c < 0.03) continue;                                                 // the gap between two cars
      R(x, y0 + 3, 1, rows, (Math.round(c * 12) % 3 === 0) ? '#7ab8c8' : '#d8f4ff');
    }
    const hx = deckX(clamp(sHead, 0, 1));                                                // its headlight
    if (sHead > 0 && sHead < 1) halo(HL.head, hx, deckTop(hx) + 4, 0.7);
  }
}
/** the deck's LED edge in the city's cyan, light running along it like water drops; drops sliding down the stays */
function drawBridgeLights(t, show) {
  let x0 = 0, yPrev = deckTop(0);
  ctx.fillStyle = show > 0.01 ? mixHex('#00a8b4', '#ff6ab0', Math.round(show * (0.5 + 0.5 * wave(t, 48)) * 8) / 8) : '#00a8b4';
  for (let x = 1; x <= DECK_END + 1; x++) {
    const y = x <= DECK_END ? deckTop(x) : -1;
    if (y !== yPrev) { if (!(x0 <= PY.x + 6 && x - 1 >= PY.x - 6)) ctx.fillRect(x0, yPrev, x - x0, 1); else { ctx.fillRect(x0, yPrev, Math.max(0, PY.x - 6 - x0), 1); ctx.fillRect(PY.x + 7, yPrev, Math.max(0, x - PY.x - 7), 1); } x0 = x; yPrev = y; }
  }
  for (let i = 0; i < 5; i++) {                                                          // the drops running along the edge
    const s = frac(6 * t / LOOP + i / 5), x = deckX(s), y = deckTop(x);
    if (x < 0 || Math.abs(x - PY.x) <= 6) continue;
    R(x, y, s > 0.5 ? 3 : 2, 1, '#c8ffff'); halo(HL.cyan, x + 1, y, 0.6);
  }
  STAYS_MAIN.concat(STAYS_BACK).forEach(([xa, ya, xb, yb], i) => {
    const { u } = cycle(t, 20, i * 0.137);
    if (u > 0.5) return;
    const v = u / 0.5, x = Math.round(lerp(xa, xb, v)), y = Math.round(lerp(ya, yb, v));
    if (Math.abs(x - PY.x) <= pyHW(y)) return;
    ctx.globalAlpha = 0.8 * Math.sin(Math.PI * v); R(x, y, 1, 1, '#a8ffff'); ctx.globalAlpha = 1;
  });
  const air = 0.5 + 0.5 * wave(t, 80);                                                   // the pylon's aviation lamp
  R(PY.x, PY.top - 1, 1, 1, air > 0.5 ? '#ff4a40' : '#7a2420'); halo(HL.red, PY.x, PY.top - 1, air);
}

/* ================= Boats ================= */
// the big two-rivers night cruise ship, bow to the left: a dark hull with a gold line, three lit decks, an open top deck
// under a lit canopy, LED outlines that change colour slowly (per frame: CRUISE_LED, offsets in the sprite)
const CRS = { w: 100, h: 26 };
const CRUISE_LED = [];
function renderCruise() {
  const p = pix(CRS.w, CRS.h), wl = CRS.h - 1;
  CRUISE_LED.length = 0;
  for (let y = 17; y <= wl; y++) for (let x = 0; x < CRS.w; x++) {                     // the hull: a raked bow (left), a square stern
    if (x < (y - 16) * 2) continue;
    if (CRS.w - 1 - x < (y > 22 ? y - 22 : 0)) continue;
    p.set(x, y, y === 17 ? '#ffd27a' : y >= wl - 1 ? '#0a0c18' : pick(['#10142a', '#181e38', '#222a48'], 0.4 + 0.2 * Math.sin(x * 0.3), x, y));
  }
  const deck = (x0, x1, y0, y1, win, col) => {
    for (let y = y0; y <= y1; y++) for (let x = x0; x <= x1; x++) {
      let c = y === y0 ? '#e8e4d8' : col;
      if (win && y > y0 && y < y1 && x % 3 !== 0 && x > x0 + 1 && x < x1 - 1) c = y === y0 + 1 ? '#ffe6a8' : '#ffc46a';
      p.set(x, y, c);
    }
  };
  deck(8, 96, 12, 16, true, '#3a3448');                                                   // the main saloon
  deck(16, 90, 8, 11, true, '#3a3448');                                                   // the second deck
  deck(26, 80, 5, 7, true, '#3a3448');                                                    // the third
  for (let x = 30; x <= 76; x++) { p.set(x, 1, '#c8a050'); if (x % 6 === 0) for (let y = 2; y <= 4; y++) p.set(x, y, '#5a4a3a'); }   // the top deck's canopy and its posts
  for (let x = 34; x <= 72; x += 4) p.set(x, 3, '#ffd890');                               // lamps strung under it
  for (let x = 18; x <= 22; x++) for (let y = 2; y <= 7; y++) p.set(x, y, y === 2 ? '#e8e4d8' : '#8ab4d8');   // the wheelhouse
  for (let y = 0; y <= 4; y++) p.set(20, y - 3 < 0 ? y : y, '#6a6a7a');
  // where the LED outlines run: along each deck's edge and the hull's sheer
  for (let x = 8; x <= 96; x += 2) CRUISE_LED.push([x, 11]);
  for (let x = 16; x <= 90; x += 2) CRUISE_LED.push([x, 7]);
  for (let x = 26; x <= 80; x += 2) CRUISE_LED.push([x, 4]);
  for (let x = 6; x <= 98; x += 3) CRUISE_LED.push([x, 18]);
  return p.done();
}
// a smaller cruise boat with blue LED trim, bow to the right
const CR2 = { w: 66, h: 19 };
function renderCruise2() {
  const p = pix(CR2.w, CR2.h), wl = CR2.h - 1;
  for (let y = 12; y <= wl; y++) for (let x = 0; x < CR2.w; x++) {
    if (CR2.w - 1 - x < (y - 11) * 1.8 || x < (y > 15 ? y - 15 : 0)) continue;
    p.set(x, y, y === 12 ? '#5ac8ff' : y >= wl - 1 ? '#0a0c18' : '#e4e6ee');
  }
  for (let x = 6; x < 54; x++) for (let y = 7; y <= 11; y++) p.set(x, y, y === 7 ? '#5ac8ff' : (x % 3 && y > 7 && y < 11) ? (y === 8 ? '#fff0c8' : '#ffd68a') : '#2a3048');
  for (let x = 14; x < 44; x++) for (let y = 3; y <= 6; y++) p.set(x, y, y === 3 ? '#5ac8ff' : (x % 4 && y === 5) ? '#ffe6b0' : '#2a3048');
  for (let x = 40; x < 46; x++) for (let y = 0; y <= 3; y++) p.set(x, y, y === 0 ? '#e8eaf0' : '#9ab8d8');
  return p.done();
}
// a little river boat, bow to the right: a white cabin, a red and a green running light
const FRY = { w: 26, h: 10 };
function renderFerry() {
  const p = pix(FRY.w, FRY.h), wl = FRY.h - 1;
  for (let y = 6; y <= wl; y++) for (let x = 0; x < FRY.w; x++) { if (FRY.w - 1 - x < (y - 6) * 1.5) continue; p.set(x, y, y === 6 ? '#e8e8f0' : y >= wl ? '#0c0e1a' : '#9aa0b8'); }
  for (let x = 4; x < 16; x++) for (let y = 2; y <= 5; y++) p.set(x, y, y === 2 ? '#e8eaf0' : (x % 3 && y === 3) ? '#ffe2a0' : '#c4c8d4');
  for (let y = 0; y < 2; y++) p.set(12, y, '#6a6a78');
  return p.done();
}
// the cruise pier on the far bank: a pontoon moored under the riverside road, its gangway, lit cabins
function paintPontoon(p) {
  const x0 = 386, x1 = 428, y = WL - 1;
  for (let x = x0; x <= x1; x++) {
    p.set(x, y + 1, '#1a1c2c'); p.set(x, y, '#3a3c54');
    for (let k = 1; k <= 4; k++) p.set(x, y - k, x % 5 === 0 ? '#2a2c40' : k === 2 ? '#ffd890' : k === 1 ? '#c89058' : '#e8e4d8');
  }
  for (let k = 0; k < 8; k++) p.set(x0 - 2 - k, y - 4 - Math.round(k * 0.4), '#6a6a80');   // the gangway up to the road
}

/* ================= The Jialing: dark water, the far side mirrored and broken, gold under Hongyadong ================= */
function renderWater() {
  const h = GND - HZ + 2, p = pix(W, h), P = ['#05061a', '#08091f', '#0c0d25', '#11122c', '#171634', '#1e1a3a'];
  for (let y = 0; y < h; y++) for (let x = 0; x < W; x++)
    p.set(x, y, pick(P, 0.55 - 0.35 * (y / h) + 0.18 * Math.exp(-(((x - 292) / 80) ** 2)) * (1 - y / h) + (vnoise(x / 40, y / 3, 9) - 0.5) * 0.16, x, y, 0.5));
  return p.done();
}
/** the far side as the river mirrors it: a still of everything over the waterline, upside down, squashed, softened */
function renderMirror(src) {
  const sx = src.getContext('2d'), data = sx.getImageData(0, 0, W, WL).data, rows = GND - WL, p = pix(W, rows);
  for (let k = 0; k < rows; k++) for (let i = 0; i < W; i++) {
    const sy = WL - 1 - k * 1.6, acc = [0, 0, 0];
    let n = 0;
    for (let dy = -3; dy <= 3; dy++) for (let dx = -2; dx <= 2; dx++) {
      const yy = Math.round(sy + dy), xx = clamp(i + dx, 0, W - 1);
      if (yy < 0 || yy >= WL) continue;
      const o = (yy * W + xx) * 4;
      acc[0] += data[o]; acc[1] += data[o + 1]; acc[2] += data[o + 2]; n++;
    }
    const f = 0.4 - k * 0.005;
    p.set(i, k, [0, 1, 2].map(c => Math.min(255, Math.floor((acc[c] / n * f + [5, 6, 20][c] * (1 - f)) / 6 + bayer(i, k)) * 6)));
  }
  return p.done();
}
const GLINTS = [];
{
  const r = rng(909);
  for (let i = 0; i < 120; i++) {
    const x = r(), y = HZ + 2 + Math.floor(r() * (GND - HZ - 3)), under = Math.abs(x * W - 292) < 64;
    GLINTS.push({ y, x, k: (r() < 0.5 ? -1 : 1) * (1 + Math.floor(r() * 3)), len: 2 + Math.floor(r() * 5), kf: 18 + Math.floor(r() * 40), ph: r(),
      c: under ? (r() < 0.7 ? '#ffb85a' : '#c87a3a') : r() < 0.4 ? '#5a6488' : r() < 0.7 ? '#3a4068' : '#01020a' });
  }
}
const STREAKS = [
  ...[240, 254, 267, 280, 293, 306, 320, 334, 347].map((x, i) => ({ x, w: 3 + (i % 2), col: i % 2 ? '#ffc060' : '#ffe09a', a: 0.5 + 0.12 * Math.exp(-(((x - 292) / 40) ** 2)), seed: i * 3 + 1, y0: WL + 1 })),
  { x: PY.x - 2, w: 4, col: '#c8d4ec', a: 0.2, seed: 13, y0: PY.base + 1 }, { x: 158, w: 2, col: '#c8d4ec', a: 0.1, seed: 14, y0: WL + 1 },
  { x: 172, w: 2, col: '#c8d4ec', a: 0.1, seed: 15, y0: WL + 1 }, { x: 406, w: 6, col: '#ffd890', a: 0.2, seed: 16, y0: WL + 1 },
  ...[136, 178, 212, 372, 445, 470].map((x, i) => ({ x, w: 2, col: '#ffe2a0', a: 0.16, seed: 20 + i, y0: WL + 1 })),
  { x: 40, w: 3, col: '#e8c890', a: 0.12, seed: 30, y0: HZ + 1 }, { x: 80, w: 2, col: '#e8c890', a: 0.1, seed: 31, y0: HZ + 1 },
];
function drawWater(t, billboard) {
  ctx.drawImage(WATER_C, 0, HZ);
  const rows = GND - WL;
  for (let k = 0; k < rows; k++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + k * 0.55) * (0.6 + k * 0.06) + Math.sin(TAU * 110 * t / LOOP - k * 1.3) * 0.6);
    ctx.globalAlpha = 0.9 * (1 - k / (rows + 14)) * (0.84 + 0.16 * Math.sin(TAU * 60 * t / LOOP + k * 1.7));
    ctx.drawImage(MIRROR_C, 0, k, W, 1, off, WL + k, W, 1);
  }
  for (let j = 0; j < 30; j++) {                                                         // the pylon's broken reflection
    const sy = PY.base - 1 - Math.round(j * 1.5), off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 0.9) * (0.6 + j * 0.08));
    if (sy < PY.top) break;
    ctx.globalAlpha = 0.34 * (1 - j / 30);
    ctx.drawImage(PYLON_C, PY.x - 8, sy, 17, 1, PY.x - 8 + off, PY.base + 2 + j, 17, 1);
  }
  for (const g of GLINTS) {
    const v = wave(t, g.kf, g.ph);
    if (v < -0.1) continue;
    ctx.globalAlpha = 0.5 * Math.min(1, v + 0.1);
    ctx.fillStyle = g.c; ctx.fillRect(Math.round(frac(g.x + g.k * t / LOOP) * (W + 30)) - 15, g.y, g.len, 1);
  }
  ctx.globalAlpha = 1;
  for (const s of STREAKS) streak(ctx, t, s.x, s.w, s.col, s.a, s.seed, s.y0, GND);
  for (const L of LEDS) if (L.front) streak(ctx, t, L.x + (L.w >> 1), 4, L.col || '#00e0e8', 0.16, 40 + L.id, WL + 1, GND);
  if (billboard) streak(ctx, t, BB.x + 26, 5, '#ff5a4a', 0.1, 21, WL + 1, GND);
}
/** a sprite on the water at row wy, its broken mirror image under it */
function drawFloat(spr, x, wy, t, a = 0.3) {
  const w = spr.width, h = spr.height, n = Math.min(h, 14);
  for (let j = 0; j < n; j++) {
    const off = Math.round(Math.sin(TAU * 45 * t / LOOP + j * 2.3 + wy) * (0.5 + j * 0.1));
    ctx.globalAlpha = a * (1 - j / n);
    ctx.drawImage(spr, 0, h - 1 - j, w, 1, x + off, wy + 1 + j, w, 1);
  }
  ctx.globalAlpha = 1;
  ctx.drawImage(spr, x, wy - h + 1);
}
function drawWake(x, wy, dir, n, t) {
  const st = step(t, 1440);
  for (let k = 0; k < n; k++) {
    if (hash(k, st, 17) > 0.84 - k * 0.012) continue;
    ctx.globalAlpha = 0.5 * (1 - k / n);
    dot(x - dir * k * 3, wy + 1 + k * 0.3, k < 5 ? '#c8d0e8' : '#6a7498');
  }
  ctx.globalAlpha = 1;
}
const LED_CYCLE = ['#3a8aff', '#a85aff', '#ff4aa8', '#ffb43a', '#3ae0e8'];
function drawBoats(t) {
  for (const F of FERRY) {                                                               // the little boat close to the far bank
    const u = inWin(t, F.t0, F.t1);
    if (u < 0) continue;
    const wy = 171, x = Math.round(F.dir > 0 ? lerp(-FRY.w - 4, W + 4, u) : lerp(W + 4, -FRY.w - 4, u));
    drawWake(F.dir > 0 ? x : x + FRY.w, wy, F.dir, 14, t);
    drawFloat(FERRY_C[F.dir > 0 ? 0 : 1], x, wy, t, 0.3);
    dot(F.dir > 0 ? x + FRY.w - 2 : x + 1, wy - 4, F.dir > 0 ? '#3aff6a' : '#ff3a3a');
    halo(HL.tiny, x + 10, wy - 6, 0.7);
  }
  let u = inWin(t, CRUISE2.t0, CRUISE2.t1);
  if (u >= 0) {                                                                          // the smaller cruise boat comes up the river
    const wy = 181, x = Math.round(lerp(-CR2.w - 10, W + 10, u));
    halo(HL.boat, x + 30, wy - 8, 0.7);
    drawWake(x, wy, 1, 20, t);
    drawFloat(CRUISE2_C, x, wy, t, 0.32);
  }
  u = inWin(t, CRUISE.t0, CRUISE.t1);
  if (u >= 0) {                                                                          // the big cruise ship sails down towards Chaotianmen
    const wy = 191, x = Math.round(lerp(W + 10, -CRS.w - 20, u)), y0 = wy - CRS.h + 1;
    halo(HL.ship, x + 50, wy - 12, 0.9);
    drawWake(x + CRS.w, wy, -1, 30, t);
    drawFloat(CRUISE_C, x, wy, t, 0.34);
    const ci = Math.floor(t / 6) % LED_CYCLE.length, cn = (ci + 1) % LED_CYCLE.length, f = smooth(0.8, 1, frac(t / 6));
    ctx.fillStyle = mixHex(LED_CYCLE[ci], LED_CYCLE[cn], Math.round(f * 8) / 8);
    const chase = step(t, 960);
    CRUISE_LED.forEach(([lx, ly], i) => { if ((i + chase) % 9) ctx.fillRect(x + lx, y0 + ly, 1, 1); });
    dot(x - 1, wy - 1, '#c8d4ec'); dot(x - 2, wy, '#8a98c0');                            // the bow wave
    for (const lx of [20, 50, 80]) streak(ctx, t, x + lx, 6, '#ffc870', 0.22, lx, wy + 2, Math.min(GND, wy + 14));
  }
}

/* ================= The promenade: wet paving, the railing, the steps down to the water ================= */
const LAMPS = [{ x: 24, y: 162 }, { x: 456, y: 162 }];  // the promenade's two tall lamps (lantern centre)
const BOLLARDS = [{ x: 100, fy: 260 }, { x: 206, fy: 262 }, { x: 318, fy: 260 }];   // low lamps along the plaza
const BENCHES = [{ x: 222, fy: 246 }, { x: 296, fy: 246 }];   // stone benches facing the river
const STEPS = { x0: 34, x1: 80, land: 191 };        // the gap in the railing and the steps down to a landing at the water
const POSTS = [];
for (let x = 2; x < W; x += 14) if (x < STEPS.x0 - 1 || x > STEPS.x1) POSTS.push(x);
POSTS.push(STEPS.x0 - 1, STEPS.x1 + 1);
function groundLight(x, y) {
  let s = 0;
  for (const L of LAMPS) s += 0.9 * Math.exp(-(Math.hypot((x - L.x) / 46, (y - 236) / 16) ** 2));
  s += 0.7 * Math.exp(-(Math.hypot((x - 424) / 30, (y - 236) / 10) ** 2));               // the chuan chuan cart's bulbs
  for (const b of BOLLARDS) s += 0.62 * Math.exp(-(Math.hypot((x - b.x) / 17, (y - b.fy) / 6) ** 2));
  return Math.min(1, s);
}
const PAVE = ['#0e0e1e', '#141428', '#1a1a30', '#22223a', '#2c2a46', '#383452'], WARMP = ['#3a2c30', '#5a3e36', '#7e563e', '#a8744a', '#d09a5a'];
function renderPromenade() {
  const p = pix(W, H - STEPS.land + 1), Y0 = STEPS.land, put = (x, y, c) => p.set(x, y - Y0, c);
  const joints = [];
  for (let y = GND + 3, g = 4; y < H; y += g, g += 1) joints.push(y);
  for (let x = 0; x < W; x++) for (let y = GND; y < H; y++) {
    const g = groundLight(x, y), d = (y - GND) / (H - GND), joint = joints.includes(y) || ((x + Math.round((y - GND) * 0.3)) % Math.round(10 + d * 22) === 0);
    let v = 0.42 + 0.12 * d + (vnoise(x / 16, y / 4, 23) - 0.5) * 0.2 - (joint ? 0.16 : 0);
    const sheen = Math.exp(-(((x - 292) / 62) ** 2)) * Math.max(0, 1 - (y - GND) / 26) * (vnoise(x / 2.5, y / 1.2, 24) > 0.45 ? 1 : 0.25);   // Hongyadong's gold in the wet stone
    v += 0.12 * sheen;
    put(x, y, g + (bayer(x, y) - 0.5) * 0.3 > 0.45 ? pick(WARMP, 0.15 + g * 0.8 + (v - 0.45) * 0.5, x, y, 0.5) : pick(PAVE, v, x, y, 0.6));
  }
  // the steps: a landing at the water, three treads up to us, low walls each side
  for (let x = STEPS.x0; x <= STEPS.x1; x++) {
    for (let y = STEPS.land; y < GND + 5; y++) {
      const tread = y < 196 ? 0 : y < 199 ? 1 : y < 201 ? 2 : 3, edge = y === 196 || y === 199 || y === 201 || y === GND + 1;
      put(x, y, edge ? '#4a4a62' : pick(PAVE, 0.35 + tread * 0.06 + (y === STEPS.land ? 0.25 : 0), x, y));
    }
  }
  for (const wx of [STEPS.x0, STEPS.x1]) for (let y = STEPS.land - 2; y < GND + 5; y++) { put(wx, y, '#2a2a40'); put(wx + (wx === STEPS.x0 ? 1 : -1), y, '#1a1a2c'); }
  return { c: p.done(), x: 0, y: Y0 };
}
function renderRailing() {
  const p = pix(W, GND - RAIL_T + 3), Y0 = RAIL_T - 2, put = (x, y, c) => p.set(x, y - Y0, c);
  for (let x = 0; x < W; x++) {
    if (x >= STEPS.x0 && x <= STEPS.x1) continue;
    put(x, RAIL_T, '#7a7890'); put(x, RAIL_T + 1, '#3a3a50'); put(x, GND - 2, '#2a2a3e');
    if (x % 3 === 0) for (let y = RAIL_T + 2; y < GND - 2; y++) put(x, y, '#141626');
  }
  for (const x of POSTS) for (let y = RAIL_T - 1; y < GND; y++) { put(x, y, y === RAIL_T - 1 ? '#9a98b0' : '#4a4a62'); put(x + 1, y, '#22222f'); }
  return { c: p.done(), x: 0, y: Y0 };
}
/* a yellow banyan, Chongqing's city tree: a broad dark crown, aerial roots hanging from its limbs, a buttressed trunk */
function renderBanyan(seed, litFrom) {
  const w = 70, h = 112, p = pix(w, h), r = rng(seed), blobs = [];
  for (let i = 0; i < 16; i++) blobs.push({ x: 8 + r() * 54, y: 8 + r() * 40, rx: 10 + r() * 12, ry: 7 + r() * 8 });
  for (let y = 0; y < 64; y++) for (let x = 0; x < w; x++) {
    let best = -9;
    for (const b of blobs) best = Math.max(best, 1 - ((x - b.x) / b.rx) ** 2 - ((y - b.y) / b.ry) ** 2);
    best += (vnoise(x / 3, y / 3, seed) - 0.5) * 0.7;
    if (best <= 0.05) continue;
    const lit = litFrom > 0 ? x / w : 1 - x / w, top = best < 0.35 && y < 30;
    const v = 0.18 + 0.35 * lit * (1 - y / 70) + (vnoise(x / 1.5, y / 1.5, seed + 1) - 0.5) * 0.35 + (top ? 0.12 : 0);
    p.set(x, y, pick(['#050a08', '#0a140e', '#122016', '#1c3020', '#2e4428', '#4a5a2c'], v, x, y, 0.7));
  }
  const tx = 35;
  for (let y = 40; y < h; y++) {                                                         // the trunk, flaring at its foot
    const hw = 3 + Math.max(0, (y - 92) * 0.35);
    for (let x = Math.round(tx - hw); x <= Math.round(tx + hw); x++) p.set(x, y, pick(['#14100e', '#221a16', '#3a2e26', '#5a4a3a'], 0.3 + (litFrom > 0 ? x - tx : tx - x) / 10 + (vnoise(x, y / 4, seed + 2) - 0.5) * 0.3, x, y));
  }
  for (let i = 0; i < 18; i++) {                                                          // aerial roots
    const rx = Math.round(10 + r() * 50), y0 = 40 + Math.round(r() * 14), len = 10 + Math.round(r() * 30);
    if (Math.abs(rx - tx) < 5) continue;
    for (let k = 0; k < len; k++) if (hash(rx, k, seed) > 0.08) p.set(rx + (k > len * 0.6 && hash(rx, 1, seed) < 0.5 ? 1 : 0), y0 + k, k > len - 3 ? '#2a221c' : '#1a1410');
  }
  return p.done();
}
function renderBollard() {
  const p = pix(5, 9);
  for (let y = 2; y < 9; y++) for (let x = 1; x < 4; x++) p.set(x, y, x === 1 ? '#3a3c50' : '#16161f');
  for (let x = 0; x < 5; x++) p.set(x, 1, '#2a2a36');
  p.set(1, 2, '#ffe8b0'); p.set(2, 2, '#fff4d8'); p.set(3, 2, '#ffd890');
  for (let x = 1; x < 4; x++) p.set(x, 0, '#22222c');
  return p.done();
}
function renderBench() {
  const p = pix(24, 7);
  for (let x = 0; x < 24; x++) { p.set(x, 0, '#8a88a0'); p.set(x, 1, '#5a5870'); p.set(x, 2, '#2e2e44'); }
  for (const lx of [2, 20]) for (let y = 3; y < 7; y++) { p.set(lx, y, '#2a2a3c'); p.set(lx + 1, y, '#1a1a28'); }
  return p.done();
}
function renderLamp() {
  const p = pix(9, 80), K = '#10121c', KH = '#3a3e52', cx = 4;
  for (let y = 8; y < 80; y++) { p.set(cx, y, K); p.set(cx - 1, y, y < 50 ? KH : '#262838'); }
  for (let y = 74; y < 80; y++) for (let x = cx - 2; x <= cx + 2; x++) p.set(x, y, x === cx - 2 ? KH : K);
  for (let y = 1; y <= 6; y++) for (let x = cx - 2; x <= cx + 2; x++) p.set(x, y, x === cx - 2 || x === cx + 2 ? K : y < 3 ? '#fff4d8' : '#ffd890');
  for (let x = cx - 3; x <= cx + 3; x++) p.set(x, 0, K);
  p.set(cx, 7, K);
  return p.done();
}
/* the chuan chuan cart: a steel cart, a glass case of skewers, a pot of red broth, a lightbox reading 串串 */
const CART = { x: 404, fy: 240 };
const CHUAN = ['...1...', '.11111.', '.1.1.1.', '.11111.', '...1...', '1111111', '1..1..1', '1111111', '...1...', '...1...'];
function renderCart() {
  const w = 46, h = 42, p = pix(w, h), by = h - 1;
  for (let x = 2; x < 42; x++) for (let y = by - 16; y <= by - 4; y++) p.set(x, y, y === by - 16 ? '#c8ccd8' : pick(['#3a3e4e', '#5a5e70', '#7a8092'], 0.5 + 0.3 * Math.sin(x * 0.4) - (y - by + 16) * 0.03, x, y));   // the steel body
  for (const wx of [8, 36]) for (let y = by - 3; y <= by; y++) for (let x = wx - 2; x <= wx + 2; x++) if (Math.hypot(x - wx, y - by + 1.5) < 2.4) p.set(x, y, '#141418');   // wheels
  for (let x = 4; x < 28; x++) for (let y = by - 22; y < by - 16; y++) {                  // the glass case and the skewers in it
    const edge = x === 4 || x === 27 || y === by - 22;
    p.set(x, y, edge ? '#a8b4c8' : (x % 2 === 0 && y > by - 21) ? ['#c83a2a', '#e8c070', '#5aa04a', '#e86a3a'][((x >> 1) + y) % 4] : '#3a4250');
  }
  for (let x = 29; x < 41; x++) for (let y = by - 21; y < by - 16; y++) p.set(x, y, y === by - 21 ? '#d8d0c8' : x === 29 || x === 40 ? '#5a5a66' : '#8a2a1a');   // the pot, its rim
  for (let x = 30; x < 40; x++) p.set(x, by - 20, x % 3 ? '#d84a22' : '#f08a3a');          // the red broth, chilli flecks
  for (let y = 0; y < 18; y++) { p.set(15, y + 2, '#2a2a34'); }                            // the lightbox's pole
  for (let y = 0; y < 13; y++) for (let x = 6; x < 26; x++) p.set(x, y, y === 0 || y === 12 || x === 6 || x === 25 ? '#5a4a3a' : '#fff0c8');
  for (const [ox] of [[8], [17]]) CHUAN.forEach((row, j) => { for (let i = 0; i < 7; i++) if (row[i] === '1') p.set(ox + i, 1 + j, '#d0281e'); });
  return p.done();
}
const BULBS = [];                                   // the string of bulbs over the cart
for (let k = 0; k <= 10; k++) BULBS.push([CART.x + 2 + k * 4, CART.fy - 30 + Math.round(2 * Math.sin(Math.PI * k / 10))]);

/* ================= People ================= */
// One function draws everyone (after rome.js and stockholm.js): a look gives the proportions for a height and the colours
// dimmed for the night; person() puts it on its feet. Autumn clothes: light jackets, the aunties' bright tops. The edge of
// the shoulders and the hair catch Hongyadong's gold across the water.
const SKINS = ['#f0c8a0', '#e0ac82', '#c88e62', '#a06c48'], HAIRS = ['#14100e', '#2a1c14', '#4a2c1c', '#6a4a2a', '#8a8078', '#c8c0b8'];
const NIGHT = '#0a0c22', GLOWC = '#ffc880';
function look(h, skin, hair, top, bot, o = {}) {
  const dk = o.dk ?? 0.3, m = c => mixHex(c, NIGHT, dk);
  const hh = h >= 22 ? 5 : h >= 14 ? 4 : 3, th = Math.round(h * 0.38);
  return { h, hh, hw: h >= 19 ? 4 : 3, th, tw: h >= 24 ? 7 : h >= 19 ? 6 : 5, ll: h - hh - th,
    skin: m(SKINS[skin]), hair: m(HAIRS[hair]), hairL: mixHex(HAIRS[hair], GLOWC, 0.35), top: m(top), topD: mixHex(top, NIGHT, dk + 0.3),
    topL: mixHex(top, GLOWC, 0.4), bot: m(bot), shoe: m('#1a161c'), long: o.long || 0, coat: o.coat || 0, cap: o.cap ? m(o.cap) : null };
}
/**
 * One person, feet on row fy, centred on column x; returns the top row of the head.
 * o.view: 0 from behind (looking at the view), 1 facing us, 2 in profile (o.dir 1 right, -1 left); o.step: stride frame
 * o.aL / o.aR: 0 down, 1 straight up, 2 bent up to the face (phone, skewer), 3 out, 4 up and out (pointing high), 5 round a
 * neighbour's back; o.lean: the head towards a neighbour; o.sit: seated; o.bend: bent forward (profile)
 */
function person(x, fy, P, o = {}) {
  x = Math.round(x); fy = Math.round(fy);
  const { hw, hh, tw, th } = P, view = o.view || 0, dir = o.dir || 1, side = view === 2;
  const legs = o.sit ? 0 : P.ll, bend = side && o.bend ? 1 : 0;
  const ty = fy - legs - th + 1 + bend, hy = ty - hh, bw = side ? tw - 2 : tw, tx = x - (bw >> 1) + bend * dir;
  if (legs) {
    const ly = fy - legs + 1;
    ctx.fillStyle = 'rgba(6,6,20,0.4)'; ctx.fillRect(x - (bw >> 1) - 2, fy + 1, bw + 4, 1); ctx.fillRect(x - (bw >> 1), fy + 2, bw, 1);
    if (side) {
      if (o.step) {
        R(x - 2, ly, 4, 2, P.bot);
        R(dir > 0 ? x + 1 : x - 2, ly + 2, 2, legs - 2, P.bot); R(dir > 0 ? x + 1 : x - 3, fy, 3, 1, P.shoe);
        R(dir > 0 ? x - 2 : x + 1, ly + 2, 2, legs - 3, P.bot); R(dir > 0 ? x - 2 : x + 1, fy - 1, 2, 1, P.shoe);
      } else { R(x - 1, ly, 2, legs, P.bot); R(dir > 0 ? x - 1 : x - 2, fy, 3, 1, P.shoe); }
    } else {
      const lw = bw >= 7 ? 3 : 2;
      R(tx, ly, bw, 2, P.bot); R(tx, ly + 2, lw, legs - 2, P.bot); R(tx + bw - lw, ly + 2, lw, legs - 2, P.bot);
      R(tx, fy, lw, 1, P.shoe); R(tx + bw - lw, fy, lw, 1, P.shoe);
    }
  } else if (o.sit) R(tx - 1, fy - 1, bw + 2, 2, P.bot);
  R(tx, ty, bw, th + P.coat, P.top);
  if (!side) R(tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  else R(dir > 0 ? tx : tx + bw - 1, ty + 1, 1, th + P.coat - 1, P.topD);
  if (view !== 1) R(tx, ty, bw, 1, P.topL);                                            // Hongyadong's light on the shoulders
  const arm = (ax, st, sg) => {
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); return [ax, ty + th - 1]; }
    if (st === 1) { R(ax, hy - 1, 1, ty - hy + 2, P.topD); R(ax, hy - 3, 1, 2, P.skin); return [ax, hy - 3]; }
    if (st === 2) { R(ax, hy + 2, 1, ty - hy, P.topD); R(ax + (sg < 0 ? 1 : -1), hy + hh - 2, 1, 2, P.skin); return [ax + (sg < 0 ? 1 : -1), hy + hh - 2]; }
    if (st === 3) { R(sg < 0 ? ax - 2 : ax, ty + 2, 3, 1, P.topD); R(sg < 0 ? ax - 3 : ax + 3, ty + 2, 1, 1, P.skin); return [sg < 0 ? ax - 3 : ax + 3, ty + 2]; }
    if (st === 4) { for (let k = 0; k < 4; k++) R(ax + sg * k, ty + 1 - k, 1, 1, P.topD); R(ax + sg * 4, ty - 3, 1, 1, P.skin); return [ax + sg * 4, ty - 3]; }
    R(sg < 0 ? ax - 4 : ax, ty + 2, 5, 1, P.topD); return [ax + sg * 4, ty + 2];
  };
  let hands;
  if (side) {
    const st = o.aR || 0, ax = x + (o.step ? -dir : 0) + bend * dir * 2;
    if (!st) { R(ax, ty + 1, 1, th - 2, P.topD); R(ax, ty + th - 1, 1, 1, P.skin); hands = [[ax, ty + th - 1]]; }
    else if (st === 2) { for (let k = 0; k < 3; k++) R(x + dir * (1 + k) + bend * dir, ty + 2 - k, 1, 1, P.topD); R(x + dir * 4 + bend * dir, ty - 1, 1, 1, P.skin); hands = [[x + dir * 4 + bend * dir, ty - 1]]; }
    else if (st === 3) { R(dir > 0 ? x + 1 : x - 3, ty + 2, 3, 1, P.topD); R(x + dir * 4, ty + 2, 1, 1, P.skin); hands = [[x + dir * 4, ty + 2]]; }
    else { R(x + dir, ty + 1, 1, 2, P.topD); R(x + dir * 2, ty, 1, 1, P.skin); hands = [[x + dir * 2, ty]]; }
  } else hands = [arm(tx - 1, o.aL, -1), arm(tx + bw, o.aR, 1)];
  const hx = x - (hw >> 1) + (o.lean || 0) + bend * dir * 2, hr = hh >= 5 ? 2 : 1, hY = hy + bend;
  if (view === 0) {
    R(hx, hY, hw, hh, P.hair); R(hx, hY, hw, 1, P.hairL);
    if (P.long) R(hx, hY + hh, hw, P.long, P.hair); else R(hx + 1, hY + hh - 1, hw - 2, 1, P.skin);
  } else if (view === 1) {
    R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair);
    if (P.long) { R(hx - 1, hY + 1, 1, hh + P.long - 1, P.hair); R(hx + hw, hY + 1, 1, hh + P.long - 1, P.hair); }
  } else { R(hx, hY, hw, hh, P.skin); R(hx, hY, hw, hr, P.hair); R(dir > 0 ? hx : hx + hw - 1, hY, 1, hh - 1 + P.long, P.hair); }
  if (P.cap) R(hx, hY - 1, hw, 2, P.cap);
  person.hands = hands;
  return hY;
}
const tyOf = (P, fy) => fy - P.ll - P.th + 1;
const L_PHOTO = look(23, 0, 0, '#d8c8b0', '#2a2a3a', { long: 3 });
const L_HE = look(24, 1, 1, '#34466e', '#1e2030');
const L_SHE = look(22, 0, 2, '#b8484e', '#2a2a3a', { long: 2 });
const L_TRIPOD = look(24, 2, 0, '#3a3a40', '#1e2030', { cap: '#2a2a30' });
const L_PHONE = look(23, 1, 0, '#4a7a6a', '#262838');
const L_LEAN = look(24, 3, 1, '#7a5a3a', '#262838');
const L_ANGLER = look(17, 2, 4, '#5a6a3a', '#2a2a30', { cap: '#3a4a2a', dk: 0.36 });
const L_VENDOR = look(24, 1, 0, '#e8e4dc', '#262838', { cap: '#e8e4dc', dk: 0.22 });
const L_EAT = [look(23, 0, 1, '#d86a4a', '#262838'), look(24, 2, 0, '#3a4a6a', '#1e2030')];
const L_DANCE = [look(23, 1, 2, '#e8508a', '#1a1a24', { dk: 0.24 }), look(22, 0, 0, '#c83a3a', '#1a1a24', { dk: 0.24 }),
  look(23, 2, 3, '#8a4ac8', '#1a1a24', { dk: 0.24 }), look(22, 1, 1, '#2aa8a0', '#1a1a24', { dk: 0.24 })];
const L_KID = look(14, 0, 0, '#f0c030', '#3a5a9a', { dk: 0.2 }), L_PARENT = look(25, 1, 1, '#6a5a8a', '#262838');
const L_WALK = [[look(27, 0, 0, '#c8b8a0', '#2a2a3a', { long: 3 }), look(26, 1, 2, '#5a3a4a', '#1e2030')],
  [look(28, 2, 1, '#2a3a5a', '#1e2030')], [look(26, 0, 0, '#e8e0f0', '#3a4a7a'), look(26, 1, 0, '#3a8a7a', '#1e2030', { long: 2 })]];
const L_SCOOT = look(27, 1, 0, '#2a5aa8', '#1e2030', { cap: '#f0c030' });

/* ================= Per-frame: the people ================= */
const PHOTO = { x: 290 }, COUPLE = { x: 206, gap: 8 }, TRIPOD = { x: 348, cam: 341 }, PHONE2 = { x: 254 }, LEANER = { x: 394 };
const ANGLER = { x: 54, fy: 195, tip: [94, 165], float: [96, 181] };
const KIDP = { x: 262, px: 273 }, SPEAKER = { x: 122, fy: 235 };
const DANCERS = [[140, LANE_A - 4], [162, LANE_A - 4], [151, LANE_A + 4], [173, LANE_A + 4]];
const EAT = { table: 372, fy: 238 };
function screenAt(x, y, w = 3, h = 2) { R(x, y, w, h, '#bfe6ff'); halo(HL.screen, x + (w >> 1), y + (h >> 1), 0.9); }
function drawPhoto(t) {
  const x = PHOTO.x, fy = EDGE, u = frac(t / 20 + 0.1), film = inWin(t, FILM[0], FILM[1]);
  if (film >= 0) {                                                                       // turned to film the cruise ship going by
    person(x, fy, L_PHOTO, { view: 2, dir: film < 0.5 ? 1 : -1, aR: 2 });
    const [hx, hy] = person.hands[0]; screenAt(hx - 1, hy - 1, 2, 3);
  } else if (u < 0.7 || (t >= SHOW.t0 + 4 && t < SHOW.t1 - 4)) {
    const hy = person(x, fy, L_PHOTO, { view: 0, aL: 1, aR: 1 });
    screenAt(x - 2, hy - 5, 4, 3);
  } else if (u < 0.86) { const hy = person(x, fy, L_PHOTO, { view: 0, aL: 2, aR: 2 }); halo(HL.screen, x, hy + 3, 0.6); }
  else person(x, fy, L_PHOTO, { view: 0 });
}
function drawCouple(t) {
  const he = COUPLE.x, she = COUPLE.x + COUPLE.gap, lean = frac(t / 40 + 0.2) < 0.2, selfie = frac(t / 60 + 0.45) < 0.1, point = t >= CRUISE.horn + 2 && t < CRUISE.horn + 10;
  if (selfie) {
    const hy = person(she, EDGE, L_SHE, { view: 0, aR: 1, lean: -1 });
    person(he, EDGE, L_HE, { view: 0, aR: 5, lean: 1 });
    screenAt(she + 2, hy - 5, 2, 3);
  } else {
    person(she, EDGE, L_SHE, { view: 0, aR: point ? 4 : 0, lean: lean ? -1 : 0 });
    person(he, EDGE, L_HE, { view: 0, aR: 5, lean: lean ? 1 : 0 });
  }
}
function drawTripod(t) {
  const tx = TRIPOD.cam, fy = EDGE;
  ctx.fillStyle = '#2a2c3a';
  for (const lx of [tx - 4, tx, tx + 3]) { const n = fy - 194; for (let k = 0; k <= n; k++) ctx.fillRect(Math.round(lerp(tx, lx, k / n)), 194 + k, 1, 1); }
  R(tx - 2, 191, 5, 3, '#16161e'); R(tx - 1, 191, 3, 1, '#3a3c4a');
  if (frac(t) < 0.5) { dot(tx + 2, 192, '#ff3030'); halo(HL.red, tx + 2, 192, 0.5); }
  const u = frac(t / 30 + 0.6);
  if (u < 0.2) { person(TRIPOD.x, fy, L_TRIPOD, { view: 2, dir: -1, bend: true, aR: 2 }); screenAt(tx - 1, 192, 2, 1); }
  else person(TRIPOD.x, fy, L_TRIPOD, { view: 0, aL: u < 0.6 ? 3 : 0 });
}
function drawPhone2(t) {
  const up = frac(t / 24 + 0.35) < 0.35, hy = person(PHONE2.x, EDGE, L_PHONE, { view: 0, aL: up ? 1 : 0, aR: up ? 1 : 0 });
  if (up) screenAt(PHONE2.x - 2, hy - 5, 4, 3);
}
function drawLeaner(t) { person(LEANER.x, EDGE, L_LEAN, { view: 0, aL: 3, aR: 3, lean: frac(t / 16) < 0.3 ? -1 : 0 }); }
function drawAngler(t) {
  const { x, fy } = ANGLER, c = inWin(t, CATCH[0], CATCH[1]), rock = inWin(t, 62, 74) >= 0 ? 1 : 0;
  R(x - 3, fy - 1, 7, 2, '#3a3a4a'); R(x - 3, fy + 1, 1, 0 + 1, '#2a2a36'); R(x + 3, fy + 1, 1, 1, '#2a2a36');   // his stool
  R(x + 8, fy - 4, 4, 5, '#3a6a9a'); R(x + 8, fy - 4, 4, 1, '#5a8aba');                   // his bucket
  person(x, fy - 1, L_ANGLER, { view: 0, sit: true, aR: 3 });
  const [hx, hy] = [x + 4, tyOf({ ll: 0, th: L_ANGLER.th }, fy - 1) + 2];
  let tip = ANGLER.tip, fl = [ANGLER.float[0], ANGLER.float[1] + Math.round(wave(t, 60) * 0.6 + rock * wave(t, 240) * 1.4)], fish = null;
  if (c >= 0) {
    if (c < 0.2) fl = [fl[0], fl[1] + (c > 0.1 ? 2 : 1)];                                 // the float dips
    else { const k = (c - 0.2) / 0.8; tip = [lerp(94, 70, k), lerp(160, 166, k)]; fish = [lerp(94, 66, k), lerp(170, 184, k * k)]; }
  }
  ctx.fillStyle = '#4a4038';
  const n = Math.max(Math.abs(tip[0] - hx), Math.abs(tip[1] - hy));
  for (let k = 0; k <= n; k++) { const s = k / n; ctx.fillRect(Math.round(lerp(hx, tip[0], s)), Math.round(lerp(hy, tip[1], s) - Math.sin(Math.PI * s) * (c >= 0.2 ? 4 : 1.5)), 1, 1); }
  const end = fish || fl;                                                                // the line, from the tip to the float or the fish
  ctx.globalAlpha = 0.5; ctx.fillStyle = '#c8d0e0';
  const m = Math.max(1, Math.abs(end[1] - tip[1]));
  for (let k = 0; k <= m; k++) ctx.fillRect(Math.round(lerp(tip[0], end[0], k / m)), Math.round(lerp(tip[1], end[1], k / m)), 1, 1);
  ctx.globalAlpha = 1;
  if (fish) { R(Math.round(fish[0]) - 1, Math.round(fish[1]), 3, 2, '#c8d4e4'); dot(fish[0] + 1, fish[1], '#ffffff'); }
  else { R(fl[0], fl[1] - 2, 1, 1, '#ff3a3a'); R(fl[0], fl[1] - 1, 1, 1, '#3aff6a'); halo(HL.float, fl[0], fl[1] - 1, 0.7); }   // the night float's lights
}
const BEAT = t => step(t, 480);                     // the dance's count: a beat every half second
function drawDancers(t) {
  const watch = t >= SHOW.t0 + 6 && t < SHOW.t1 - 12, b = BEAT(t), cnt = b % 8, turn = (b % 60) >= 52;
  const POSES = [[3, 3], [1, 1], [3, 3], [0, 0], [4, 0], [0, 4], [1, 3], [3, 1]];
  // the little speaker on the ground, its ring pulsing on the beat
  R(SPEAKER.x - 2, SPEAKER.fy - 6, 5, 7, '#1a1a24'); R(SPEAKER.x - 2, SPEAKER.fy - 6, 5, 1, '#3a3a4a');
  const pulse = 1 - frac(t * 2);
  R(SPEAKER.x - 1, SPEAKER.fy - 4, 3, 3, pulse > 0.5 ? '#5ad8ff' : '#2a6aa8'); halo(HL.cyan, SPEAKER.x, SPEAKER.fy - 3, 0.3 + 0.5 * pulse);
  DANCERS.forEach(([x, fy], i) => {
    const P = L_DANCE[i];
    if (watch) { person(x, fy, P, { view: 0, aR: i === 1 && frac(t / 6) < 0.5 ? 4 : 0, lean: i === 2 ? 1 : 0 }); return; }
    const [aL, aR] = POSES[cnt], sway = cnt === 4 ? -1 : cnt === 5 ? 1 : 0;
    person(x + sway, fy, P, { view: turn ? 0 : 1, aL, aR });
    if (i < 2) for (const [hx, hy] of person.hands) { R(hx - 1, hy - 1, 2, 2, '#e8303a'); dot(hx - 1, hy - 1, '#ff8a8a'); }   // red fans
  });
}
function drawKid(t) {
  const look = frac(t / 18) < 0.3;
  person(KIDP.px, LANE_A, L_PARENT, { view: look ? 2 : 0, dir: -1, aR: look ? 3 : 0 });
  const hy = person(KIDP.x, LANE_A, L_KID, { view: look ? 1 : 0, aR: 1, aL: look ? 3 : 0 });
  const [hx, hy2] = person.hands[1], a = TAU * frac(t * 1.5);                           // a light-up spinner held high
  for (let k = 0; k < 3; k++) { const ang = a + k * TAU / 3; dot(hx + Math.round(Math.cos(ang) * 2), hy2 - 2 + Math.round(Math.sin(ang)), ['#ff4aa8', '#4ae8ff', '#ffe04a'][k]); }
  halo(HL.toy, hx, hy2 - 2, 0.7);
  return hy;
}
function drawEaters(t, here) {
  const tx = EAT.table, fy = EAT.fy;
  R(tx, fy - 9, 22, 2, '#5a4a3a'); R(tx + 2, fy - 7, 1, 7, '#3a2e26'); R(tx + 19, fy - 7, 1, 7, '#3a2e26');   // the low folding table
  for (const sx of [tx - 6, tx + 25]) { R(sx, fy - 4, 5, 1, '#a83a30'); R(sx, fy - 3, 1, 3, '#5a1a16'); R(sx + 4, fy - 3, 1, 3, '#5a1a16'); }   // low red plastic stools
  if (!here) return;
  R(tx + 8, fy - 12, 5, 3, '#d8d0c8'); R(tx + 9, fy - 12, 3, 1, '#c8401e');              // a bowl of red oil, a cup of sticks
  for (let k = 0; k < 4; k++) dot(tx + 15 + k, fy - 13 - (k & 1), '#c8a878');
  [[tx - 3, 1], [tx + 27, -1]].forEach(([x, dir], i) => {
    const eat = frac(t / (5 + i) + i * 0.4) < 0.3;
    const P = L_EAT[i];
    R(x + dir * 2 - (dir < 0 ? 1 : 0), fy - 5, 2, 1, P.bot); R(x + dir * 3 - (dir < 0 ? 1 : 0), fy - 4, 1, 4, P.bot); R(x + dir * 3 - (dir < 0 ? 1 : 0), fy, 2, 1, P.shoe);   // knees and shins under the table
    person(x, fy - 4, P, { view: 2, dir, sit: true, aR: eat ? 2 : 3 });
    if (eat) { const [hx, hy] = person.hands[0]; for (let k = 1; k <= 3; k++) dot(hx + dir * k, hy - k, '#c8a878'); }   // a skewer to the mouth
  });
  const { u } = cycle(t, 80, 0.3);                                                        // steam off the bowl
  ctx.globalAlpha = 0.35 * (1 - u); dot(tx + 10 + wave(t, 30), fy - 13 - u * 6, '#d8dce8'); ctx.globalAlpha = 1;
}
function drawStall(t, crowd) {
  const x = CART.x, fy = CART.fy, here = !nobody(crowd);
  if (here) {                                                                             // the vendor behind the cart, stirring the pot
    const stir = frac(t / 4) < 0.5;
    person(x + 34, fy - 8, L_VENDOR, { view: 1, aR: stir ? 3 : 2, aL: 3 });
  }
  ctx.drawImage(CART_C, x, fy - 41);
  for (let k = 0; k < 5; k++) {                                                           // the pot steams
    const { u } = cycle(t, 60, k / 5), rr = 1 + Math.floor(u * 3);
    ctx.globalAlpha = 0.3 * Math.min(1, u * 5) * (1 - u);
    ctx.drawImage(PUFFS[rr - 1], Math.round(x + 35 + wave(t, 24, k * 0.2) * 2 * u) - rr, Math.round(fy - 22 - u * 18) - rr);
  }
  ctx.globalAlpha = 1;
  BULBS.forEach(([bx, by], i) => { dot(bx, by, '#ffe6a8'); if (i % 3 === 0) halo(HL.bulb, bx, by, 0.7 + 0.1 * wave(t, 120, i * 0.1)); });
  halo(HL.sign, x + 16, fy - 35, 0.7);
}
function drawWalkers(t, keep) {
  for (const Wk of WALKS) {
    const u = inWin(t, Wk.t0, Wk.t1);
    if (u < 0 || !keep(Wk.who)) continue;
    const d = Wk.dir, x = Math.round(d > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u)), f = Math.floor(x / 4) & 1, Ls = L_WALK[Wk.who];
    Ls.forEach((P, i) => {
      const px = x - d * i * 9, fy = LANE_B + i * 2;
      person(px, fy, P, { view: 2, dir: d, step: (f + i) & 1, aR: Wk.who === 2 ? 2 : Wk.who === 0 && i === 0 ? 3 : 0 });
      if (Wk.who === 2) { const [hx, hy] = person.hands[0]; R(hx - 1, hy - 2, 2, 3, '#e8d8c0'); dot(hx - 1, hy - 3, '#3a2a2a'); }   // bubble tea
      if (Wk.who === 0 && i === 0) { const [hx, hy] = person.hands[0]; R(hx - 1, hy, 3, 4, '#c83a4a'); }   // a shopping bag
    });
  }
}
function drawScooter(t) {
  for (const S of SCOOT) {
    const u = inWin(t, S.t0, S.t1);
    if (u < 0) continue;
    const d = S.dir, x = Math.round(d > 0 ? lerp(-30, W + 30, u) : lerp(W + 30, -30, u)), fy = LANE_B + 4;
    R(x - 7, fy - 1, 3, 2, '#141418'); R(x + 5, fy - 1, 3, 2, '#141418');                 // wheels
    R(x - 6, fy - 3, 13, 2, '#2a5aa8'); R(x + d * 6, fy - 12, 1, 10, '#3a3a48');           // the deck, the stem
    R(x - d * 3 - 2, fy - 9, 5, 5, '#f0c030');                                            // a delivery box behind the seat
    person(x, fy - 3, L_SCOOT, { view: 2, dir: d, sit: true, aR: 3 });
    dot(x + d * 7, fy - 11, '#fff6dc'); halo(HL.head, x + d * 9, fy - 10, 0.8);
  }
}

/* ================= The drizzle, the city's own: three depths, the near drops catching the lamps ================= */
const DRZ = [
  { count: 120, nMin: 90, nMax: 110, len: 2, slope: 0.1, col: '#7a78a4', a: 0.2, y1: [96, WL - 2], lit: false },
  { count: 80, nMin: 110, nMax: 130, len: 2, slope: 0.14, col: '#a8a6cc', a: 0.36, y1: [WL + 2, GND - 2], lit: true },
  { count: 46, nMin: 140, nMax: 160, len: 3, slope: 0.2, col: '#c4c4e4', a: 0.44, y1: [H + 4, H + 4], lit: true },
];
DRZ.forEach((L, li) => { const r = rng(500 + li); L.drops = []; for (let i = 0; i < L.count; i++) L.drops.push({ id: i + li * 1000, n: L.nMin + Math.floor(r() * (L.nMax - L.nMin + 1)), ph: r() }); });
const drizzleLevel = t => smooth(-0.15, 0.6, wave(t, 2, 0.792));   // two spells a loop, peaking at 55 and 175 s
const DZ_LIGHTS = [[291, 130, 74, 40, '#ffc070'], [LAMPS[0].x, 186, 30, 44, '#ffd890'], [LAMPS[1].x, 186, 30, 44, '#ffd890'], [424, 214, 26, 22, '#ffd890'], [PY.x, 96, 12, 50, '#c8d8f0']];
function drawDrizzle(depth, t, level) {
  const L = DRZ[depth], n = Math.round(L.count * level);
  if (n <= 0) return;
  for (let i = 0; i < n; i++) {
    const d = L.drops[i], { cyc, u } = cycle(t, d.n, d.ph);
    const yl = lerp(L.y1[0], L.y1[1], hash(d.id, cyc, 12)), x0 = -20 + hash(d.id, cyc, 11) * (W + 40), y = -6 + u * (yl + 6), x = x0 - (yl - y) * L.slope;
    let col = L.col, a = L.a;
    if (L.lit) for (const [cx, cy, rx, ry, lc] of DZ_LIGHTS) { const dx = (x - cx) / rx, dy = (y - cy) / ry; if (dx * dx + dy * dy < 1) { col = lc; a = 0.6; break; } }
    ctx.globalAlpha = a * (L.y1[0] < H && u > 0.9 ? (1 - u) * 10 : 1); ctx.fillStyle = col;
    for (let k = 0; k < L.len; k++) ctx.fillRect(Math.round(x - k * L.slope), Math.round(y - k), 1, 1);
  }
  ctx.globalAlpha = 1;
}
function drawRings(t, level) {                                                           // the drizzle's rings on the river
  const n = Math.round(40 * level);
  ctx.fillStyle = '#6a6890';
  for (let i = 0; i < n; i++) {
    const { cyc, u } = cycle(t, 140 + (i % 7) * 3, i * 0.071), x = Math.round(hash(i, cyc, 31) * W), y = Math.round(lerp(WL + 3, GND - 2, hash(i, cyc, 32))), r = 1 + Math.round(u * 2);
    ctx.globalAlpha = 0.45 * (1 - u);
    ctx.fillRect(x - r, y, 1, 1); ctx.fillRect(x + r, y, 1, 1); if (r > 1) { ctx.fillRect(x - 1, y - 1, 3, 1); ctx.fillRect(x - 1, y + 1, 3, 1); }
  }
  ctx.globalAlpha = 1;
}
// a picked weather (site/js/engine/weather.js): Chongqing draws its own drizzle; the fog stays in every pick
const WEATHER = weatherPlans(['drizzle']);
const WX_LIGHTS = DZ_LIGHTS.map(([x, y, rx, ry, col]) => ({ x, y, rx, ry, col }));
const WX = { land: [[96, WL - 2], [WL + 2, GND - 1], [GND + 6, H - 2]], lights: WX_LIGHTS };
const WX_BB = { ...WX, lights: [...WX_LIGHTS, { x: BB.x + 26, y: BB.y + 10, rx: 34, ry: 20, col: '#ff8a6a' }] };

/* ================= The hotpot sign on the tower behind Hongyadong (the city's one billboard): MALA MOON HOTPOT ================= */
// A lit panel high on a tower wall: the shop's name in cream, HOTPOT in chilli red, a steaming pot under a crescent moon.
function renderBoard() {
  const p = pix(W, H), L = BB.x - 1, Rr = BB.x + BB.w, T0 = BB.y - 1, B = BB.y + BB.h;
  for (let y = T0; y <= B; y++) for (let x = L; x <= Rr; x++) p.set(x, y, y === T0 ? '#5a5a70' : x === L || y === B || x === Rr ? '#16161f' : '#0a0a12');
  for (const bx of [BB.x + 8, BB.x + BB.w - 9]) { p.fill(bx, T0 - 2, 2, 2, '#22222e'); p.fill(bx, B + 1, 2, 2, '#22222e'); }   // its brackets on the wall
  return trim(p);
}
function renderAd(steam, dim) {
  const { w, h } = BB, p = pix(w, h), lit = [];
  for (let j = 0; j < h; j++) for (let i = 0; i < w; i++) p.set(i, j, (i === 0 || j === 0 || i === w - 1 || j === h - 1) ? '#24161e' : '#100a12');
  const letters = (s, x, y, col) => { let cx = x; for (const ch of s) { const g = FONT3[ch]; if (g) g.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[i] === '1') lit.push([cx + i, y + j, col]); }); cx += 4; } };
  letters('MALA MOON', 3, 3, '#ffe6c4');
  letters('HOTPOT', 3 + Math.round((textWidth('MALA MOON') - textWidth('HOTPOT')) / 2), 11, dim ? '#7a2a24' : '#ff4a36');
  for (const [x, y] of [[44, 2], [45, 1], [46, 1], [44, 3], [44, 4], [45, 5], [46, 5]]) lit.push([x, y, '#ffd25a']);   // a crescent moon
  for (let x = 40; x <= 50; x++) lit.push([x, 12, '#c83a2a']);                          // the pot: its rim, the bowl, two handles
  for (let x = 41; x <= 49; x++) { lit.push([x, 13, '#a82a20']); lit.push([x, 14, '#a82a20']); }
  for (let x = 42; x <= 48; x++) lit.push([x, 15, '#a82a20']);
  lit.push([39, 12, '#c83a2a'], [51, 12, '#c83a2a']);
  for (let x = 41; x <= 49; x += 2) lit.push([x, 11, '#ff8a3a']);                       // the red broth bubbling over
  const S = [['.1.', '1..', '.1.', '..1'], ['1..', '.1.', '..1', '.1.'], ['..1', '.1.', '1..', '.1.']][steam];
  for (const ox of [42, 46]) S.forEach((row, j) => { for (let i = 0; i < 3; i++) if (row[(i + (ox === 46 ? 1 : 0)) % 3] === '1') lit.push([ox + i, 6 + j, '#e8e4f0']); });
  const on = new Set(lit.map(([x, y]) => x + ',' + y));
  for (const [x, y, col] of lit) for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const X = x + dx, Y = y + dy;
    if ((!dx && !dy) || on.has(X + ',' + Y) || X < 1 || Y < 1 || X > w - 2 || Y > h - 2) continue;
    p.set(X, Y, mixHex('#100a12', col, 0.22));
  }
  for (const [x, y, col] of lit) p.set(x, y, col);
  return gradeAd(p.done(), { level: 0.86, tint: '#140c1e', mix: 0.1, wear: 0.12, seed: 29 });
}
const BB_FLICK = [[66.2, 67.4], [201.6, 202.4]];    // HOTPOT's red tubes stutter now and then
function drawBoard(t, pitch) {
  const fl = BB_FLICK.some(([a, b]) => t >= a && t < b) && hash(step(t, 2400), 13) < 0.5;
  halo(HL.sign2, BB.x + 26, BB.y + 10, fl ? 0.45 : 0.85);
  blit(BOARD_C);
  if (!PITCH.covers(t, pitch)) ctx.drawImage(ADS[step(t, 360) % 3][fl ? 1 : 0], BB.x, BB.y);
  PITCH.draw(ctx, t, BB.x, BB.y, pitch);
}

/* ================= Per-frame: LED facades, the towers' light show, the fog ================= */
const LEDPAL = ['#00c8d4', '#2a62f0', '#8a46f0', '#f0389a', '#f0a030', '#30d88a'];
const palAt = v => { v = ((v % 6) + 6) % 6; const i = Math.floor(v), f = Math.round((v - i) * 6) / 6; return mixHex(LEDPAL[i], LEDPAL[(i + 1) % 6], f); };
const palIdx = v => Math.round(((v % 6) + 6) % 6) % 6;
function drawOwnLED(L, t, k) {
  const v = L.hue * 6 + t * 12 / LOOP, col = palAt(v);
  L.col = col; L.ci = palIdx(v);
  ctx.fillStyle = col;
  if (L.mode === 0) {                                                                    // bands rising up the face
    ctx.globalAlpha = 0.12 * k; ctx.fillRect(L.x, L.y, L.w, L.h);
    ctx.globalAlpha = 0.42 * k;
    for (let j = 0; j < L.h; j++) if (frac(j / 7 + t * 24 / LOOP + L.ph) < 0.45) ctx.fillRect(L.x, L.y + j, L.w, 1);
  } else if (L.mode === 1) {                                                             // its edges pulsing, a grid between
    const pulse = 0.55 + 0.45 * wave(t, 30, L.ph);
    ctx.globalAlpha = 0.55 * pulse * k;
    ctx.fillRect(L.x, L.y, 1, L.h); ctx.fillRect(L.x + L.w - 1, L.y, 1, L.h); ctx.fillRect(L.x, L.y, L.w, 1);
    ctx.globalAlpha = 0.16 * k;
    for (let j = 3; j < L.h; j += 4) ctx.fillRect(L.x + 1, L.y + j, L.w - 2, 1);
  } else {                                                                               // a bright bar sweeping across a soft fill
    ctx.globalAlpha = 0.12 * k; ctx.fillRect(L.x, L.y, L.w, L.h);
    ctx.globalAlpha = 0.5 * k;
    const s = frac(t * 16 / LOOP + L.ph);
    if (L.w >= 8) ctx.fillRect(L.x + Math.round(s * (L.w - 2)), L.y, 2, L.h); else ctx.fillRect(L.x, L.y + Math.round(s * (L.h - 2)), L.w, 2);
  }
  ctx.globalAlpha = 1;
}
/** the show's colour and level at column x: a wave sweeping right to left, bands rising in step, a gold finale */
function showLight(x, s) {
  if (s < 4 || s >= 42) return null;
  if (s < 20) {
    const sx = lerp(540, -80, (s - 4) / 16), d = x - sx;
    return { col: palAt(2.6 + d / 70), a: (0.12 + 0.5 * Math.exp(-((d / 55) ** 2))) * smooth(4, 6, s), bands: false };
  }
  if (s < 34) return { col: palAt(3 - (s - 20) * 0.2), a: 0.5, bands: true };
  return { col: '#c88a3a', a: 0.22 * smooth(34, 36, s) * (1 - smooth(40, 42, s)), bands: false, gold: true };
}
function drawLEDs(t) {
  const s = t - SHOW.t0, inShow = s >= 0 && s < SHOW.t1 - SHOW.t0;
  for (const L of LEDS) {
    const own = inShow ? (s < 4 ? 1 - s / 4 : s > 42 ? (s - 42) / 4 : 0) : 1;
    if (own > 0.01) drawOwnLED(L, t, own);
    const sh = inShow && showLight(L.x + L.w / 2, s);
    if (!sh) continue;
    ctx.fillStyle = sh.col; L.col = sh.col;
    if (sh.bands) {
      ctx.globalAlpha = 0.1; ctx.fillRect(L.x, L.y, L.w, L.h); ctx.globalAlpha = sh.a;
      for (let j = 0; j < L.h; j++) if (frac((L.y + j) / 10 + s * 0.55) < 0.42) ctx.fillRect(L.x, L.y + j, L.w, 1);
    } else { ctx.globalAlpha = sh.a; ctx.fillRect(L.x, L.y, L.w, L.h); }
    if (sh.gold) {                                                                       // the finale: the facades twinkle gold
      ctx.globalAlpha = Math.min(1, 3 * sh.a); ctx.fillStyle = '#ffe0a0';
      const n = Math.round(L.w * L.h / 14);
      for (let k = 0; k < n; k++) if (hash(L.id, step(t, 1440), k) < 0.5) ctx.fillRect(L.x + Math.floor(hash(L.id, k, 5) * L.w), L.y + Math.floor(hash(L.id, k, 6) * L.h), 1, 1);
    }
    ctx.globalAlpha = 1;
  }
}
function drawAir(t) {                                                                    // the towers' red lamps, blinking together
  const a = smooth(-0.2, 0.6, wave(t, 80));
  if (a < 0.05) return;
  ctx.globalAlpha = a;
  for (const [x, y] of AIR) dot(x, y, '#ff3a30');
  ctx.globalAlpha = 1;
  for (let i = 0; i < AIR.length; i += 2) halo(HL.red, AIR[i][0], AIR[i][1], a * 0.8);
}
function drawRaffles(t) {
  blit(RAFF_C);
  const s = t - SHOW.t0, sh = s >= 0 && s < 46 ? showLight(172, s) : null;
  ctx.fillStyle = sh ? sh.col : palAt(1 + t * 6 / LOOP); ctx.globalAlpha = sh ? 0.4 + sh.a * 0.6 : 0.42;
  ctx.fillRect(CRYSTAL.x0 + 2, CRYSTAL.y1, CRYSTAL.x1 - CRYSTAL.x0 - 3, 1); ctx.fillRect(CRYSTAL.x0 + 1, CRYSTAL.y1 - 1, CRYSTAL.x1 - CRYSTAL.x0 - 1, 1);
  ctx.globalAlpha = 1;
  halo(HL.crystal, 172, 84, 0.7 + 0.2 * wave(t, 4));
}
// the bank rolls in from upstream (the right) over 26 s, lies in the tops for 24 s, then drifts on and thins
const fogBank = t => {
  if (t < FOG.t0 + 4 || t >= FOG.t1) return null;
  const x = t < FOG.in + 16 ? lerp(W + 10, -120, smooth(FOG.t0 + 4, FOG.in + 16, t)) : t < FOG.full ? lerp(-120, -170, (t - FOG.in - 16) / (FOG.full - FOG.in - 16)) : lerp(-170, -330, (t - FOG.full) / (FOG.t1 - FOG.full));
  return { x: Math.round(x), a: smooth(FOG.t0 + 4, FOG.t0 + 12, t) * (1 - smooth(FOG.full + 6, FOG.t1, t)) };
};
function drawFogLayer(F, t, a) {
  if (a <= 0.01) return;
  const off = scroll(t, F.k, W);
  ctx.globalAlpha = Math.min(1, a); ctx.drawImage(F.c, -off, F.y); ctx.drawImage(F.c, W - off, F.y); ctx.globalAlpha = 1;
}
function drawTopFog(t) {
  drawFogLayer(FOGS[0], t, 0.6 + 0.2 * wave(t, 3, 0.1) + 0.3 * env(t, FOG.t0, FOG.t1, 14));
  const b = fogBank(t);
  if (!b) return;
  ctx.globalAlpha = b.a; ctx.drawImage(BANK_C, b.x, -4); ctx.globalAlpha = 1;
  for (const L of LEDS) {                                                                // the LED facades glow through it
    if (!L.front && L.id >= 200) continue;
    const cx = L.x + (L.w >> 1), cover = smooth(b.x + 40, b.x + 190, cx) * (1 - smooth(b.x + 570, b.x + 720, cx));
    if (cover > 0.05) halo(HL.led[L.ci ?? 0], cx, L.y + Math.min(14, L.h >> 1), 0.8 * cover * b.a);
  }
}

/* ================= Per-frame: Hongyadong, the riverside road ================= */
function drawHongyadong(t) {
  halo(HL.hy, 291, 130, 0.92 + 0.06 * wave(t, 6));
  blit(HY_C);
  ctx.fillStyle = '#ff4a34';
  for (const L of HY_LANTERNS) ctx.fillRect(L.x + (hash(L.id, step(t, 120), 3) < 0.06 ? 1 : 0), L.y, 1, 2);
  ctx.fillStyle = '#6a2a12';
  for (const w of HY_FLICK) if (hash(w.id, step(t, 240), 5) < 0.28) ctx.fillRect(w.x, w.y, 1, 2);
  halo(HL.hyTop, 291, 116, 0.55 + 0.05 * wave(t, 10, 0.3));
}
function drawRoad(t) {
  for (const [d, k, ph] of ROAD_CARS) {
    const u = frac(k * t / LOOP + ph), x = Math.round(d < 0 ? lerp(W + 4, RF.x0 - 8, u) : lerp(RF.x0 - 8, W + 4, u));
    R(x, ROAD + 1, 2, 1, d < 0 ? '#fff2d8' : '#ff4a3a');
    if (d < 0) halo(HL.tiny, x, ROAD + 1, 0.5);
  }
}
const winLit = (w, t) => w.mode === 'on' || (w.mode === 'tog' && frac(w.k * t / LOOP + w.ph) < w.duty);
function drawWins(a, b, t) {
  for (let i = a; i < b; i++) {
    const w = WINS[i];
    if (!winLit(w, t)) continue;
    ctx.fillStyle = w.c1; ctx.fillRect(w.x, w.y, w.w, w.h);
  }
}

/* ================= Per-frame: the promenade and the people on it ================= */
const WET = [                                        // the lamps and Hongyadong mirrored in the wet stone: [x, w, colour, alpha, seed, y0, y1]
  ...[244, 262, 280, 298, 316, 334].map((x, i) => [x, 3, i % 2 ? '#ffb850' : '#ffd070', 0.2, 60 + i * 3, GND + 1, GND + 22]),
  [LAMPS[0].x, 5, '#ffd890', 0.22, 71, 240, H], [LAMPS[1].x, 5, '#ffd890', 0.22, 72, 240, H], [CART.x + 20, 10, '#ffd890', 0.16, 73, CART.fy + 2, H],
  ...BOLLARDS.map((b, i) => [b.x, 3, '#ffc880', 0.2, 74 + i, b.fy + 1, H]),
  [124, 3, '#5ad8ff', 0.18, 79, 236, 262], [430, 3, '#ff6a5a', 0.12, 80, 215, 236],
];
function drawPromenade(t, crowd) {
  blit(TERR_C);
  for (const [x, w, col, a, seed, y0, y1] of WET) streak(ctx, t, x, w, col, a, seed, y0, y1);
  blit(RAIL_C);
  POSTS.forEach((x, i) => { if (i % 3 === 1) { dot(x, RAIL_T - 2, '#ffe2a8'); halo(HL.post, x, RAIL_T - 2, 0.8); } });   // small lamps on the posts
  const DL = [], put = (fy, fn) => DL.push([fy, fn]), here = rank => !nobody(crowd) && (rank === 0 || rank < crowd);
  if (here(RANK.angler)) put(ANGLER.fy, () => drawAngler(t));
  if (here(RANK.couple)) put(EDGE, () => drawCouple(t));
  if (here(RANK.photo)) put(EDGE + 0.1, () => drawPhoto(t));
  if (here(RANK.tripod)) put(EDGE + 0.2, () => drawTripod(t));
  if (here(RANK.phone)) put(EDGE + 0.3, () => drawPhone2(t));
  if (here(RANK.leaner)) put(EDGE + 0.4, () => drawLeaner(t));
  if (here(RANK.dance)) put(LANE_A - 4, () => drawDancers(t));
  if (here(RANK.kid)) put(LANE_A, () => drawKid(t));
  put(EAT.fy, () => drawEaters(t, here(RANK.eaters)));
  put(CART.fy, () => drawStall(t, crowd));
  const walkRank = [RANK.walk0, RANK.walk1, RANK.walk2];
  put(LANE_B, () => drawWalkers(t, who => here(walkRank[who])));
  if (here(RANK.scoot)) put(LANE_B + 4, () => drawScooter(t));
  put(244, () => ctx.drawImage(TREE_C[0], 6 - 35, 244 - 111));
  put(246, () => ctx.drawImage(TREE_C[1], 472 - 34, 246 - 111));
  for (const L of LAMPS) put(250, () => ctx.drawImage(LAMP_C, L.x - 4, L.y - 3));
  for (const b of BOLLARDS) put(b.fy, () => { ctx.drawImage(BOLL_C, b.x - 2, b.fy - 8); halo(HL.boll, b.x, b.fy - 6, 0.85); });
  for (const b of BENCHES) put(b.fy, () => { R(b.x + 1, b.fy + 1, 22, 1, 'rgba(6,6,20,0.4)'); ctx.drawImage(BENCH_C, b.x, b.fy - 6); });
  DL.sort((a, b) => a[0] - b[0]);
  for (const [, fn] of DL) fn();
  for (const L of LAMPS) { const I = 0.94 + 0.04 * wave(t, 900, L.x * 0.01); halo(HL.lamp, L.x, L.y + 4, I); halo(HL.lampCore, L.x, L.y, I); }
  for (let i = 0; i < 14; i++) {                                                         // banyan leaves catching the lamps as the wind stirs them
    const side = i < 7 ? 0 : 1, a = hash(i, 7, 77), lx = side ? 446 + a * 30 : 2 + a * 34, ly = 140 + hash(i, 8, 77) * 50;
    if (wave(t, 20 + (i % 5) * 4, a) > 0.55) dot(lx, ly, side ? '#6a7a3a' : '#5a6a32');
  }
}
/* ================= Prerendered state ================= */
let HL, SKY_C, CLOUDS, FOGS, BANK_C, NAN_C, TOW_C, RAFF_C, RIDGE_C, HY_C, BRIDGE_C, PYLON_C, WATER_C, MIRROR_C, TERR_C, RAIL_C, TREE_C, LAMP_C,
  CART_C, CRUISE_C, CRUISE2_C, FERRY_C, PUFFS, VIG_C, BOARD_C, ADS, PITCH, BOLL_C, BENCH_C;
const SPLIT = {};
function renderVignette() {
  const p = pix(W, H);
  for (let y = 0; y < H; y++) for (let i = 0; i < W; i++) {
    const v = smooth(0.74, 1.45, Math.hypot((i - W / 2) / (W / 2), (y - H * 0.48) / (H / 2) * 0.9)), q = Math.floor(v * 4 + bayer(i, y)) / 4;
    if (q > 0) p.set(i, y, [5, 6, 18], Math.round(q * 0.42 * 255));
  }
  return p.done();
}
function makePuff(r, col) {
  const s = r * 2 + 1, p = pix(s, s);
  for (let y = 0; y < s; y++) for (let i = 0; i < s; i++) { const d = Math.hypot(i - r, y - r) / r; if (d < 1 && (1 - d) * 1.6 > bayer(i, y) * 0.9) p.set(i, y, y < r * 0.7 ? col : mixHex(col, '#3a3c52', 0.35)); }
  return p.done();
}
function prerender() {
  WINS.length = HY_LANTERNS.length = HY_FLICK.length = 0;
  HL = {
    hy: makeHalo(92, '#ff9a40', 0.17, 1.6), hyTop: makeHalo(46, '#ffc060', 0.12, 1.5), head: makeHalo(3, '#fff4d8', 0.5, 1.1),
    cyan: makeHalo(4, '#40ffff', 0.4, 1.2), red: makeHalo(3, '#ff4040', 0.5, 1), screen: makeHalo(4, '#9ad0ff', 0.4, 1.2),
    float: makeHalo(3, '#80ff90', 0.5, 1.1), toy: makeHalo(5, '#ffa0e0', 0.35, 1.2), bulb: makeHalo(8, '#ffd890', 0.3, 1.3),
    sign: makeHalo(14, '#ffe0a0', 0.25, 1.4), lamp: makeHalo(52, '#ffc070', 0.2, 1.7), lampCore: makeHalo(12, '#fff0c8', 0.5, 1.3),
    tiny: makeHalo(3, '#fff4d0', 0.5, 1.1), boat: makeHalo(30, '#6ac8ff', 0.12, 1.5), ship: makeHalo(48, '#ffb060', 0.14, 1.5),
    pylon: makeHalo(40, '#c8d8ff', 0.1, 1.6), sign2: makeHalo(30, '#ff6a4a', 0.1, 1.5), crystal: makeHalo(30, '#a8d0ff', 0.08, 1.6),
    post: makeHalo(5, '#ffd890', 0.4, 1.2), boll: makeHalo(14, '#ffc880', 0.22, 1.4), led: LEDPAL.map(c => makeHalo(16, c, 0.22, 1.5)),
  };
  SKY_C = renderSky(); CLOUDS = CLOUD_DEFS.map(makeCloudLayer); FOGS = FOG_DEFS.map(makeFog); BANK_C = makeFogBank();
  const r = rng(2026);
  buildTowers(r);
  SPLIT.nan = WINS.length; { const p = pix(W, H); paintNanan(p, r); NAN_C = trim(p); }
  for (const [x0, w, top] of NAN_LED) LEDS.push({ x: x0 + 1, y: top + 3, w: w - 2, h: 22, mode: 2, hue: (x0 * 0.13) % 1, ph: hash(x0, 1, 72), id: 200 + x0, front: false });
  SPLIT.tow = WINS.length; { const p = pix(W, H); for (const T of TOWERS) paintTower(p, r, T); TOW_C = trim(p); }
  { const p = pix(W, H); paintRaffles(p); RAFF_C = trim(p); }
  SPLIT.ridge = WINS.length; { const p = pix(W, H); paintRidge(p, r); RIDGE_C = trim(p); }
  SPLIT.end = WINS.length;
  { const p = pix(W, H); paintRoad(p); paintWings(p); paintHongyadong(p, r); paintPontoon(p); HY_C = trim(p); }
  BRIDGE_C = renderBridge(true); PYLON_C = renderBridge(false);
  BOARD_C = renderBoard();
  ADS = [0, 1, 2].map(s => [renderAd(s, false), renderAd(s, true)]);
  PITCH = makePitch(BB.w, BB.h, { accent: ACCENT });
  WATER_C = renderWater();
  {                                                                                      // the mirror's source: the still far side
    const [c, x] = makeCanvas(W, WL);
    x.drawImage(SKY_C, 0, 0);
    for (const L of [NAN_C, RAFF_C, TOW_C, RIDGE_C, HY_C]) x.drawImage(L.c, L.x, L.y);
    for (const w of WINS) if (w.mode === 'on') { x.fillStyle = w.c1; x.fillRect(w.x, w.y, w.w, w.h); }
    MIRROR_C = renderMirror(c);
  }
  TERR_C = renderPromenade(); RAIL_C = renderRailing();
  TREE_C = [renderBanyan(31, 1), flipC(renderBanyan(47, 1))];
  LAMP_C = renderLamp(); CART_C = renderCart(); BOLL_C = renderBollard(); BENCH_C = renderBench();
  CRUISE_C = renderCruise(); CRUISE2_C = renderCruise2();
  FERRY_C = [renderFerry(), null]; FERRY_C[1] = flipC(FERRY_C[0]);
  PUFFS = [1, 2, 3, 4].map(rr => makePuff(rr, '#9a9cb4'));
  VIG_C = renderVignette();
}

/* ================= Frame compositor (back to front) ================= */
// pitch: the "your ad here" face (promo captures) on the hotpot sign; billboard: false leaves the sign out (the loop
// videos); weather: a WEATHERS pick (site/js/engine/weather.js), the city's own when left out; crowd: the share of the
// people to show, 0..1 (CROWD when left out or not a number); 0 = nobody at all
function renderFrame(t, { pitch, billboard = true, weather, reducedMotion = false, crowd: share } = {}) {
  t = ((t % LOOP) + LOOP) % LOOP;
  ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.globalAlpha = 1; ctx.globalCompositeOperation = 'source-over';
  const wx = WEATHER(weather), wxc = billboard ? WX_BB : WX;
  // An explicit pick stays wet; only the city's own weather takes breaks between showers.
  const drizzle = weather === 'drizzle' ? 1 : drizzleLevel(t);
  const crowd = clamp(typeof share === 'number' && share === share ? share : CROWD, 0, 1);
  const fogUp = env(t, FOG.t0, FOG.t1, 16);
  ctx.drawImage(SKY_C, 0, 0);
  for (const L of CLOUDS) { const off = scroll(t, L.k, W); ctx.drawImage(L.c, -off, L.y); ctx.drawImage(L.c, W - off, L.y); }
  blit(NAN_C); drawWins(SPLIT.nan, SPLIT.tow, t);
  drawRaffles(t);
  blit(TOW_C); drawWins(SPLIT.tow, SPLIT.ridge, t);
  drawLEDs(t); drawAir(t);
  drawTopFog(t);
  drawFogLayer(FOGS[1], t, 0.32 + 0.14 * wave(t, 2, 0.4) + 0.3 * fogUp);
  if (billboard) drawBoard(t, pitch);
  blit(RIDGE_C); drawWins(SPLIT.ridge, SPLIT.end, t);
  if (wx.drizzle) drawDrizzle(0, t, drizzle);
  drawWeather(ctx, wx.shared, 0, t, wxc, reducedMotion);
  drawHongyadong(t);
  drawRoad(t);
  drawWater(t, billboard);
  drawFogLayer(FOGS[2], t, 0.42 + 0.18 * wave(t, 4, 0.7) + 0.2 * fogUp);
  halo(HL.pylon, PY.x, PY.deck + 6, 0.9);
  blit({ c: BRIDGE_C, x: 0, y: 0 });
  drawDeckTraffic(t); drawTrain(t);
  drawBridgeLights(t, (t >= SHOW.t0 + 20 && t < SHOW.t0 + 34) ? env(t, SHOW.t0 + 20, SHOW.t0 + 34, 2) : 0);
  drawBoats(t);
  if (wx.drizzle) { drawRings(t, drizzle); drawDrizzle(1, t, drizzle); }
  drawWeather(ctx, wx.shared, 1, t, wxc, reducedMotion);
  drawPromenade(t, crowd);
  if (wx.drizzle) drawDrizzle(2, t, drizzle);
  drawWeather(ctx, wx.shared, 2, t, wxc, reducedMotion);
  ctx.drawImage(VIG_C, 0, 0);
}

export default {
  id: 'chongqing',
  name: 'Chongqing',
  tagline: 'Fog and neon over Hongyadong',
  timeZone: 'Asia/Shanghai',
  accent: '#00fff0',                                   // the bridge's LED cyan, written out: tools/seo/seo.mjs reads it from the source
  focusX: 291,
  landmark: [HY.x0 - 1, HY.x1],                         // Hongyadong's front, eave to eave (the hotpot sign stands within it)
  landmarkTop: 98,                                     // the finial on the tall pavilion
  billboard: BB,
  crowd: CROWD,                                        // the share of the people shown by default; render(t, { crowd }) thins them, 0 = nobody
  ambience: { rain: 0.22, water: 0.35, traffic: 0.32, crowd: 0.14, wind: 0.08 },
  events: [
    { t: CRUISE.horn, type: 'horn', x: 440, gain: 0.45 },
    { t: CRUISE.horn + 1, type: 'boat', dur: 82, pan: [0.9, -0.9], gain: 0.34 },
    ...TRAINS.map(([t0, t1, d]) => ({ t: t0 + 1, type: 'train', dur: t1 - t0 - 2, pan: d < 0 ? [-0.1, -0.9] : [-0.9, -0.1], gain: 0.24 })),
    { t: FERRY[0].t0 + 1, type: 'boat', dur: 22, pan: [0.9, -0.9], gain: 0.2 },
    { t: CATCH[0] + 4.2, type: 'splash', x: ANGLER.tip[0], gain: 0.3 },
    { t: CRUISE2.horn, type: 'horn', x: 40, gain: 0.3 },
    { t: CRUISE2.horn + 1, type: 'boat', dur: 68, pan: [-0.9, 0.9], gain: 0.26 },
    { t: SHOW.t0 + 4, type: 'chime', x: 360, gain: 0.25 },
    { t: FERRY[1].t0 + 1, type: 'boat', dur: 22, pan: [-0.9, 0.9], gain: 0.2 },
  ],
  create(c) {
    ctx = c;
    ctx.imageSmoothingEnabled = false;
    prerender();
    return { render: renderFrame };
  },
};
